<script setup>
import {
  ref,
  watch,
  onMounted,
  computed,
  onBeforeUnmount,
  nextTick,
} from "vue";
import {
  toTitleCase,
  stripHtml,
  unwrapPdfLineBreaks,
} from "@/utils/stringUtils";
// Mesma regra de bloco do PlaintextToHtml do backend: usada para pré-preencher
// o editor rich text com o conteúdo de um arquivo carregado, quando o texto já
// é HTML — antes de qualquer salvar.
import { plaintextToEditorHtml } from "@/utils/tiptapEditorConfig";
// Extração de PDF: o navegador não lê o formato, então quem extrai é a API.
// Só extrai — não cria nem altera texto nenhum.
import { extractTextFromFile } from "@/services/textService";
import api from "@/services/api";
import { buildCoverFromFile } from "@/utils/coverImage";
import { useTextStore } from "@/stores/useTextStore";
import { useAuthorStore } from "@/stores/useAuthorStore";
import { useCategoryStore } from "@/stores/useCategoryStore";
import { useTopicStore } from "@/stores/useTopicStore";
import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getPublicModeHint,
} from "@/utils/permissionDefaults";
import AddAuthorForm from "../Author/AddAuthorForm.vue";
import AddCategoryForm from "@/components/Category/AddCategoryForm.vue";
import AddTopicForm from "@/components/Topic/AddTopicForm.vue";
import AddPermissionForm from "../Generics/AddPermissionForm.vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import debounce from "lodash.debounce";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import RichTextEditor from "../Generics/RichTextEditor.vue";
import AddTextLimitModal from "./AddTextLimitModal.vue";
import PlanCatalogByName from "@/components/User/PlanCatalogByName.vue";
import TopicCardModal from "@/components/Topic/TopicCardModal.vue";
import AuthorCardModal from "@/components/Author/AuthorCardModal.vue";
import CategoryCardModal from "@/components/Category/CategoryCardModal.vue";
// Mesmo modal do botão "Converter para HTML" no leitor (TextDisplayer) — ver
// processFiles: importar EPUB/DOCX num texto ainda plaintext dispara este
// ensaio antes de aceitar o conteúdo.
import ConvertTextToHtmlModal from "@/components/Displayer/ConvertTextToHtmlModal.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  textId: Number,
  // Campo que deve receber o cursor ao abrir ("author" | "category" |
  // "topics"), vindo do item em aberto clicado na confirmação do cadastro.
  focusField: { type: String, default: null },
});

const emit = defineEmits(["close", "text-updated"]);

// State
// Uma tela só, sem passos, como no cadastro rápido: título e conteúdo à vista,
// e a catalogação (subtítulo, fonte, autor, gênero, assuntos) recolhida em
// "Mais informações".
const showDetails = ref(false);
const detailsPanelRef = ref(null);

function toggleDetails() {
  if (isLoading.value || isSubmitting.value) return;
  showDetails.value = !showDetails.value;

  // Aberta, a seção costuma nascer abaixo da dobra do modal.
  if (showDetails.value) {
    nextTick(() =>
      detailsPanelRef.value?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
    );
  }
}

const isSubmitting = ref(false);
const isLoading = ref(true); // true até carregarDados() concluir

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// 🔹 NOVO: expandir/colapsar o editor de conteúdo no step 2 (espelho do AddNoteForm)
const isBodyExpanded = ref(false);
function handleToggleBodyExpand() {
  if (isLoading.value || isSubmitting.value) return;
  isBodyExpanded.value = !isBodyExpanded.value;
}
const authorStore = useAuthorStore();
const categoryStore = useCategoryStore();
const topicStore = useTopicStore();
const textStore = useTextStore();

// Qual editor de conteúdo montar. Texto já em HTML ganha o editor formatado;
// texto puro CONTINUA na caixa de texto — abrir o editor rich text sobre conteúdo
// plano faria uma conversão implícita, irreversível e sem ensaio. Converter é ato
// deliberado, pelo botão do Sys. O backend recusa a combinação, de todo modo.
const isHtmlContent = computed(
  () => textStore.selectedText?.contentFormat === "html"
);

/**
 * O conteúdo a enviar. Em HTML vai como está — aplicar `stripHtml` aqui, como o
 * ramo de texto puro faz, destruiria justamente a formatação que se quer editar.
 */
function contentToSend() {
  const value = obj.value.pastedContent || "";
  return isHtmlContent.value ? value : stripHtml(value);
}
const groupStore = useGroupStore();
const permissionStore = usePermissionStore();
const userStore = useUserStore();

const obj = ref({ permissions: [] });

// ── Capa ─────────────────────────────────────────────────────────────────────
// Três estados convivem aqui, e é a distinção entre eles que impede um
// salvamento comum de apagar a capa que já existe:
//
//   coverBlob != null  → imagem nova escolhida agora
//   removeCover        → o usuário pediu para tirar a capa
//   nenhum dos dois    → não mexeu; o backend mantém o que está gravado
const coverBlob = ref(null);
const coverPreview = ref("");
const coverError = ref("");
const removeCover = ref(false);
const isProcessingCover = ref(false);

// Capa já gravada, montada a partir do caminho relativo que a API devolve.
const existingCoverUrl = computed(() => {
  const path = obj.value?.coverPath;
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = (api.defaults.baseURL || "")
    .replace(/\/api$/, "")
    .replace(/\/+$/, "");
  return `${base}/${path.replace(/^\/+/, "")}`;
});

// O que a miniatura mostra: a nova, se houver; senão a gravada, a menos que o
// usuário tenha pedido para remover.
const coverThumbUrl = computed(() => {
  if (coverPreview.value) return coverPreview.value;
  if (removeCover.value) return "";
  return existingCoverUrl.value;
});

function releaseCoverPreview() {
  // Object URL não é coletado enquanto não for revogado.
  if (coverPreview.value) URL.revokeObjectURL(coverPreview.value);
  coverPreview.value = "";
}

async function onCoverChange(event) {
  const file = event.target.files?.[0];
  // Zera o input: sem isso, escolher o MESMO arquivo depois de remover não
  // dispara change de novo.
  event.target.value = "";
  if (!file) return;

  coverError.value = "";
  isProcessingCover.value = true;
  try {
    const cover = await buildCoverFromFile(file);
    releaseCoverPreview();
    coverBlob.value = cover.blob;
    coverPreview.value = cover.previewUrl;
    // Escolher imagem nova cancela um pedido de remoção anterior.
    removeCover.value = false;
  } catch (e) {
    coverError.value = e.message || "Não foi possível processar a imagem.";
  } finally {
    isProcessingCover.value = false;
  }
}

function clearCover() {
  releaseCoverPreview();
  coverBlob.value = null;
  coverError.value = "";
  // Só vira pedido de remoção se havia capa gravada; descartar uma imagem que
  // ainda não foi salva não precisa dizer nada ao backend.
  removeCover.value = !!obj.value?.coverPath;
}

onBeforeUnmount(releaseCoverPreview);

// Teto da sinopse. Espelha o StringLength(2000) do TextUpdateDto e o tamanho da
// coluna: os três precisam continuar iguais, senão o formulário promete um
// limite que a API recusa.
const SYNOPSIS_MAX_LENGTH = 2000;

const synopsisLength = computed(() => (obj.value.synopsis ?? "").length);

// 90% do teto: cedo o bastante para dar tempo de encurtar, tarde o bastante
// para não incomodar quem escreve um resumo normal.
const synopsisNearLimit = computed(
  () => synopsisLength.value >= SYNOPSIS_MAX_LENGTH * 0.9
);

// 🔹 Estado para mapear os erros de validação da API
const fieldErrors = ref({});

// Agora autor e gênero trabalham em modo "multiple + max=1" (para poder usar #tag como em topics)
const selectedAuthor = ref([]);
const selectedCategory = ref([]);
const selectedTopics = ref([]);

const fileName = ref("");
const inputMethod = ref("paste");

const showAddAuthorModal = ref(false);
const showAddCategoryModal = ref(false);
const showAddTopicModal = ref(false);
const newAuthorName = ref("");
const newCategoryName = ref("");
const newTopicName = ref("");

const searchTerm = ref("");
const currentPage = ref(1);
const pageSize = ref(30);

const authorList = ref([]);
const categoryList = ref([]);
const topicList = ref([]);

const fileInputRef = ref(null);
const titleFocusRef = ref(null);
const authorFocusRef = ref(null);
const categoryFocusRef = ref(null);
const topicFocusRef = ref(null);

// ─── MINI-MODAL DE PERMISSÕES (espelho de EditActivityForm.vue) ───────────────
const showPermissionModal = ref(false);
const permissionBackup = ref([]);
const permissionFormKey = ref(0);
const permissionFormRef = ref(null);

// 🔹 NOVO: arrasto próprio do mini-modal, independente do painel principal.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return (
    groupStore.groups.find((g) => g.groupName?.toLowerCase() === "todos")
      ?.groupId ?? null
  );
}
 
function getPermissionIdByName(name) {
  return (
    permissionStore.permissions.find(
      (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
    )?.permissionId ?? null
  );
}
 
function buildPublicPermissions() {
  const todosId = getTodosGroupId();
  const creatorId = userStore.currentUser?.userId ?? null;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
 
  const perms = [];
  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }
  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }
  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}
 
function buildPrivatePermissions() {
  const creatorId = userStore.currentUser?.userId ?? null;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  if (!creatorId || !visualizarId || !selecionarId || !editarId) return [];
  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}
 
function permsMatch(a, b) {
  if (a.length !== b.length) return false;
  const key = (p) => `${p.type}|${p.entityId}|${p.permissionId}`;
  const setA = new Set(a.map(key));
  return b.every((p) => setA.has(key(p)));
}
 
// currentAccessMode — computed de 3 estados
const currentAccessMode = computed(() => {
  const perms = obj.value.permissions || [];
  if (perms.length === 0) return "public";
  const publicPerms = buildPublicPermissions();
  if (publicPerms.length > 0 && permsMatch(perms, publicPerms)) return "public";
  const privatePerms = buildPrivatePermissions();
  if (privatePerms.length > 0 && permsMatch(perms, privatePerms)) return "private";
  return "custom";
});
 
function selectAccessMode(mode) {
  if (isLoading.value || isSubmitting.value) return;
  if (mode === "public") {
    obj.value.permissions = buildPublicPermissions();
  } else if (mode === "private") {
    obj.value.permissions = buildPrivatePermissions();
  } else if (mode === "custom") {
    openPermissionModal();
  }
}

// ── Visibilidade condensada ("Visibilidade: Público ▾") ─────────────────────
// Mesma apresentação do cadastro rápido (AddTextForm): os três modos de sempre,
// resolvidos por currentAccessMode / selectAccessMode, num gatilho do tamanho
// do próprio rótulo em vez de três botões ocupando meia linha.
const VISIBILITY_OPTIONS = [
  {
    value: "public",
    label: "Público",
    get hint() {
      return getPublicModeHint();
    },
    icon: "fa-globe-americas",
    colorClass: "visibility--public",
  },
  {
    value: "private",
    label: "Privado",
    hint: "Somente você",
    icon: "fa-lock",
    colorClass: "visibility--private",
  },
  {
    value: "custom",
    label: "Personalizado",
    hint: "Pessoas e grupos que você escolher",
    icon: "fa-user-shield",
    colorClass: "visibility--custom",
  },
];

function visibilityMeta(mode) {
  return (
    VISIBILITY_OPTIONS.find((o) => o.value === mode) ?? VISIBILITY_OPTIONS[0]
  );
}

const isVisibilityMenuOpen = ref(false);
const visibilityDropdownRef = ref(null);

function toggleVisibilityMenu() {
  if (isLoading.value || isSubmitting.value) return;
  isVisibilityMenuOpen.value = !isVisibilityMenuOpen.value;

  // O painel abre para BAIXO e o .modal-body rola: perto do fim da rolagem ele
  // nasceria fora da parte visível. Rolar o gatilho para dentro da vista traz o
  // painel junto, porque ele é filho posicionado do gatilho.
  if (!isVisibilityMenuOpen.value) return;
  nextTick(() => {
    visibilityDropdownRef.value?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  });
}

function handleVisibilityOption(mode) {
  isVisibilityMenuOpen.value = false;
  selectAccessMode(mode);
}

function handleVisibilityClickOutside(event) {
  if (
    isVisibilityMenuOpen.value &&
    !event.target.closest(".visibility-dropdown")
  ) {
    isVisibilityMenuOpen.value = false;
  }
}

function openPermissionModal() {
  permissionBackup.value = (obj.value.permissions || []).map((p) => ({ ...p }));
  showPermissionModal.value = true;
}

function cancelPermissionModal() {
  obj.value.permissions = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}

function confirmPermissionModal() {
  if (
    permissionFormRef.value &&
    !permissionFormRef.value.validateBeforeConfirm()
  ) {
    return;
  }
  showPermissionModal.value = false;
}
// ─────────────────────────────────────────────────────────────────────────────

const creatorUserName = ref("");

// TopicCardModal
const showTopicCardModal = ref(false);
const selectedTopicIdForModal = ref(null);

function openTopicCard(topicOption) {
  if (!topicOption?.value) return;
  selectedTopicIdForModal.value = topicOption.value;
  showTopicCardModal.value = true;
}

function closeTopicCard() {
  showTopicCardModal.value = false;
  selectedTopicIdForModal.value = null;
}

// AuthorCardModal
const showAuthorCardModal = ref(false);
const selectedAuthorIdForModal = ref(null);

function openAuthorCard(authorOption) {
  if (!authorOption?.value) return;
  selectedAuthorIdForModal.value = authorOption.value;
  showAuthorCardModal.value = true;
}

function closeAuthorCard() {
  showAuthorCardModal.value = false;
  selectedAuthorIdForModal.value = null;
}

// CategoryCardModal
const showCategoryCardModal = ref(false);
const selectedCategoryIdForModal = ref(null);

function openCategoryCard(categoryOption) {
  if (!categoryOption?.value) return;
  selectedCategoryIdForModal.value = categoryOption.value;
  showCategoryCardModal.value = true;
}

function closeCategoryCard() {
  showCategoryCardModal.value = false;
  selectedCategoryIdForModal.value = null;
}

// SYS BADGE
function isSysEntity(entityLike) {
  const name =
    entityLike?.createdByUserName ||
    entityLike?.CreatedByUserName ||
    entityLike?.createdByUser ||
    entityLike?.CreatedByUser;
  return typeof name === "string" && name.toUpperCase() === "SYS";
}

function toOptionWithSys({ label, value, isSys = false, ...rest }) {
  return { label, value, isSys: !!isSys, ...rest };
}

// TOAST
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

// Ao salvar com sucesso, esconde só o painel do modal imediatamente (o componente
// segue montado até o emit("close"), então o toast — que fica num Teleport próprio —
// continua visível sozinho, sem o modal sobreposto atrás.
const hideBackdropForSuccess = ref(false);

const TOAST_MS = 2000;

/**
 * Erro fica o TRIPLO do tempo — 6s contra os 2s do sucesso.
 *
 * Não é preferência: aviso de erro costuma ser uma instrução de várias linhas,
 * não uma confirmação. "Envie um PDF com texto selecionável, faça OCR antes"
 * precisa ser lido inteiro para servir de alguma coisa, e um aviso que some
 * antes de ser lido não avisou nada. Sucesso segue curto: ali a mensagem
 * confirma o que o usuário acabou de fazer, e ele já sabe o que diz.
 */
const TOAST_ERROR_MS = TOAST_MS * 3;

function resetToastTimeout(durationMs = TOAST_MS) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, durationMs);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(TOAST_ERROR_MS);
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
  if (typeof onFinish === "function") {
    // Esconde o painel do modal já — o toast segue visível sozinho até o
    // onFinish (emit close), que desmonta o componente após o tempo do toast.
    //
    // A MESMA constante do toast de sucesso, de propósito: fechar antes cortaria
    // a mensagem no ar, e fechar depois deixaria o modal vazio na tela.
    hideBackdropForSuccess.value = true;
    setTimeout(() => onFinish(), TOAST_MS);
  }
}

// Texto salvo com catalogação ainda em aberto. Enquanto existir, o modal
// mostra a confirmação no lugar do formulário — mesma tela do cadastro rápido,
// com os campos que faltam à mão.
const savedText = ref(null);

/** Botão "Concluir" da tela de confirmação, focado quando ela aparece. */
const finishFocusRef = ref(null);

// Ícones iguais aos da barra lateral (Autores, Gêneros, Assuntos): o chip leva
// ao mesmo assunto que aqueles itens de menu, e vale ler como a mesma coisa.
const CATALOG_FIELDS = [
  { field: "author", label: "Autor", icon: "fa-pen-nib" },
  { field: "category", label: "Gênero", icon: "fa-book-open" },
  { field: "topics", label: "Assuntos", icon: "fa-lightbulb" },
];

function pendingCatalogFields() {
  const missing = [];
  if (!obj.value.authorId) missing.push("author");
  if (!obj.value.categoryId) missing.push("category");
  if (!(obj.value.topicIds || []).length) missing.push("topics");
  return CATALOG_FIELDS.filter((item) => missing.includes(item.field));
}

// Desfecho do salvar: sobrando catalogação em aberto, o modal fica na
// confirmação oferecendo completar; não sobrando, é o toast de sempre e o modal
// se fecha sozinho — uma tela a mais sem nada a oferecer seria só atrito.
function finishSave(message) {
  const pending = pendingCatalogFields();

  if (pending.length) {
    savedText.value = { title: obj.value.title, message, pending };
    isSubmitting.value = false;

    // "Concluir" recebe o foco: é a única ação do rodapé nesta tela, e quem
    // acabou de salvar com o teclado espera fechar com Enter, sem procurar o
    // botão. Os itens em aberto acima continuam alcançáveis por Tab.
    nextTick(() => finishFocusRef.value?.focus());
    return;
  }

  showSuccessToast(message, () => {
    emit("text-updated");
    emit("close");
    isSubmitting.value = false;
  });
}

// "Concluir": a lista do pai só é recarregada quando o modal sai de cena.
function finishAndClose() {
  emit("text-updated");
  emit("close");
}

// Um item em aberto devolve o formulário, com o cursor no campo escolhido.
function resumeEditing(field) {
  savedText.value = null;
  focusCatalogField(field);
}

// Confirmação de impacto em notas
const showImpactModal = ref(false);
const impactResult = ref(null);
const pendingUpdatePayload = ref(null);
const noteDecisions = ref({});

const criticalImpactedNotes = computed(() => {
  if (!impactResult.value || !impactResult.value.impactedNotes) return [];
  return impactResult.value.impactedNotes.filter(
    (n) => n.impactType !== "RangeShiftOnly"
  );
});

// MODAL DE LIMITE DO PLANO
const showTextLimitModal = ref(false);
const textLimitInfo = ref({
  currentUsage: null,
  limit: null,
  newUsage: null,
  message: "",
});

// Catálogo de planos
const showPlanCatalogModal = ref(false);

function openPlansFromLimitModal() {
  showImpactModal.value = false;
  showTextLimitModal.value = false;
  showPlanCatalogModal.value = true;
}

function handlePlanSelect(payload) {
  console.log("Plano selecionado no catálogo (edição):", payload);
  showPlanCatalogModal.value = false;
}

function handlePlanCatalogClose() {
  showPlanCatalogModal.value = false;
}

// Computed lists
const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({
    id: u.userId,
    name: u.userName,
    photoUrl:
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      u.userPhotoPath ??
      u.user_photo_path ??
      null,
    photoPath:
      u.photoPath ??
      u.photo_path ??
      u.photoUrl ??
      u.photo_url ??
      null,
    userPhotoPath:
      u.userPhotoPath ??
      u.user_photo_path ??
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      null,
  }))
);

const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId: g.createdByUserId ?? g.CreatedByUserId ?? null,
    createdByUserName: g.createdByUserName ?? g.CreatedByUserName ?? null,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);

const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id: p.permissionId,
    name: p.permissionName,
  }))
);

function closeModal() {
  emit("close");
}

function focusTopicMultiselect() {
  nextTick(() => {
    requestAnimationFrame(() => {
      const root = topicFocusRef.value?.$el;
      const input =
        root?.querySelector("input") ||
        root?.querySelector(".multiselect__input");
      input?.focus();
    });
  });
}

// Carregamento principal
async function carregarDados() {
  try {
    await textStore.getTextById(props.textId);
    const t = textStore.selectedText;
    if (!t) return;

    const textPermissions = await textStore.fetchTextPermissions(t.textId);
    const formattedPermissions = (textPermissions || []).map((p) => ({
      entityId: p.userId != null ? p.userId : p.groupId,
      permissionId: p.permissionId,
      type: p.userId != null ? "user" : "group",
    }));

    creatorUserName.value = t.createdByUserName;

    const allPermissions = permissionStore.permissions || [];
    const creator =
      (userStore.users || []).find((u) => u.userName === t.createdByUserName) ||
      null;

    let mergedPermissions = [...formattedPermissions];
    if (creator && allPermissions.length > 0) {
      allPermissions.forEach((perm) => {
        const exists = mergedPermissions.some(
          (p) =>
            p.type === "user" &&
            p.entityId === creator.userId &&
            p.permissionId === perm.permissionId
        );
        if (!exists) {
          mergedPermissions.push({
            entityId: creator.userId,
            type: "user",
            permissionId: perm.permissionId,
          });
        }
      });
    }

    // Assuntos
    const topicIdsFromApi = Array.isArray(t.topicIds)
      ? t.topicIds
      : Array.isArray(t.topicsId)
      ? t.topicsId
      : Array.isArray(t.topics)
      ? t.topics
          .map((x) => x.topicId ?? x.id ?? x.value)
          .filter((x) => x != null)
      : t.topicId
      ? [t.topicId]
      : [];

    obj.value = {
      textId: t.textId,
      title: t.title,
      subtitle: t.subtitle || "",
      synopsis: t.synopsis || "",
      coverPath: t.coverPath || "",
      source: t.source || "",
      active: t.active,
      authorId: t.authorId,
      categoryId: t.categoryId,
      topicIds: topicIdsFromApi,
      pastedContent: textStore.fileContent || "",
      permissions: mergedPermissions,
    };

    selectedAuthor.value =
      t.authorId != null
        ? [
            {
              label: t.authorName,
              value: t.authorId,
              isSys:
                authorList.value.find((x) => x.value === t.authorId)?.isSys ??
                false,
            },
          ]
        : [];

    selectedCategory.value =
      t.categoryId != null
        ? [
            {
              label: t.categoryName,
              value: t.categoryId,
              isSys:
                categoryList.value.find((x) => x.value === t.categoryId)
                  ?.isSys ?? false,
            },
          ]
        : [];

    const topicsOptionsFromApi = Array.isArray(t.topics) ? t.topics : [];

    selectedTopics.value = topicIdsFromApi
      .map((id) => {
        const fromList = topicList.value.find((x) => x.value === id);
        if (fromList) return fromList;

        const fromApi = topicsOptionsFromApi.find(
          (x) => (x.topicId ?? x.id ?? x.value) === id
        );

        if (fromApi) {
          return toOptionWithSys({
            label: fromApi.topicName ?? fromApi.name ?? fromApi.label ?? "",
            value: id,
            description: fromApi.description ?? "",
            isSys: isSysEntity(fromApi),
          });
        }

        return toOptionWithSys({
          label: `Assunto #${id}`,
          value: id,
          description: "",
          isSys: false,
        });
      })
      .filter(Boolean);

    inputMethod.value = obj.value.pastedContent ? "paste" : "file";

    // Texto que veio do cadastro rápido chega sem catalogação — e é exatamente
    // o que os itens em aberto do cadastro trazem o usuário até aqui para
    // fazer. Nesse caso a seção nasce aberta; num texto já catalogado, recolhida.
    showDetails.value =
      !!props.focusField ||
      !obj.value.authorId ||
      !obj.value.categoryId ||
      (obj.value.topicIds?.length ?? 0) === 0;
  } catch (err) {
    console.error("Erro ao mapear dados para o formulário:", err);
  }
}

// Abre o campo pedido já em edição: o multiselect ganha o foco e a lista, em
// vez de o usuário ter de procurar o campo e clicar de novo.
function focusCatalogField(field) {
  const refByField = {
    author: authorFocusRef,
    category: categoryFocusRef,
    topics: topicFocusRef,
  };
  const target = refByField[field];
  if (!target) return;

  showDetails.value = true;

  nextTick(() => {
    requestAnimationFrame(() => {
      const instance = target.value;
      instance?.$el?.scrollIntoView({ block: "center", behavior: "smooth" });
      // activate() é do vue-multiselect: foca o input e abre a lista.
      instance?.activate?.();
    });
  });
}

function setInputMethod(method) {
  inputMethod.value = method;
  fieldErrors.value.file = null;
  fieldErrors.value.pastedContent = null;

  // 🔹 NOVO: no modo arquivo o editor some — garante o colapso do estado de expansão
  if (method === "file") {
    isBodyExpanded.value = false;

    // Escolher "Importar arquivo" JÁ abre o seletor: sem a zona de arrasto, o
    // botão é a ação inteira, e obrigar a um segundo clique numa tela que não
    // oferece mais nada seria só uma etapa a pedir.
    //
    // SEMPRE, inclusive com arquivo já carregado — é assim que se troca de
    // arquivo agora. O link "Trocar arquivo" que fazia esse papel saiu: o botão
    // do segmento já é o lugar óbvio para voltar, e ter os dois era oferecer
    // duas portas para a mesma sala.
    nextTick(() => openFilePicker());
  }

  if (method !== "file") {
    // Voltar para "Colar Texto" DESFAZ a importação: o conteúdo na caixa era o
    // do arquivo, e mantê-lo aqui seria oferecer para edição um texto que o
    // usuário acabou de dispensar — sem nome de arquivo na tela dizendo de onde
    // veio, já que `fileName` some logo abaixo. Mesma regra do AddTextForm.
    //
    // ⚠️ Aqui a caixa fica VAZIA, e não com o conteúdo que o texto tinha antes
    // da importação: ele não é guardado em lugar nenhum: `obj.pastedContent`
    // nasce de `textStore.fileContent` no carregamento e a importação escreve
    // por cima. Quem quiser o conteúdo original de volta fecha a edição sem
    // salvar — nada foi gravado até ali.
    //
    // Só quando havia arquivo carregado, e é por isso que o `fileName` é lido
    // ANTES de ser limpo. Sem esse guarda, o caminho do cancelamento
    // (handleFilePickerCancel → setInputMethod("paste")) apagaria o conteúdo do
    // texto em edição — desistir de importar não pode custar o que já estava lá.
    if (fileName.value) obj.value.pastedContent = "";

    fileName.value = "";
    if (fileInputRef.value) fileInputRef.value.value = "";
  }
}

/**
 * Abre o seletor nativo de arquivos.
 *
 * O `<input type="file">` fica escondido porque a caixa que o navegador desenha
 * não combina com o resto do formulário; quem dá a aparência é o botão que
 * chama daqui.
 */
function openFilePicker() {
  if (isExtracting.value) return;
  fileInputRef.value?.click();
}

/**
 * Seletor fechado sem escolher nada.
 *
 * Sem arquivo, o modo arquivo não tem o que mostrar — e como abrir o seletor é
 * automático ao entrar nele, cancelar deixava o usuário num beco: modo aceso,
 * nada carregado. O modo arquivo só permanece depois que um arquivo entra de
 * fato.
 *
 * O guarda `!fileName` cobre a TROCA de arquivo: clicar em "Importar" com um já
 * carregado reabre o seletor, e desistir ali tem de manter o que estava — não
 * jogar o usuário de volta ao modo colar.
 *
 * O evento `cancel` do `<input type="file">` é a API própria para isto. Onde ele
 * não existir, nada acontece — o usuário continua no modo arquivo e clica de
 * novo em "Importar", que é degradação aceitável, não quebra.
 */
function handleFilePickerCancel() {
  if (!fileName.value) setInputMethod("paste");
}

const onSearch = debounce(async (searchText) => {
  searchTerm.value = searchText;
  currentPage.value = 1;
  await authorStore.fetchAuthorsPaged(
    currentPage.value,
    pageSize.value,
    searchTerm.value
  );
  authorList.value = authorStore.authors.map((a) => ({
    label: a.authorName,
    value: a.authorId,
    isSys: isSysEntity(a),
  }));
}, 400);

/**
 * Altura da caixa do editor, a mesma nos dois modos e igual à do AddTextForm.
 *
 * Antes cada modo produzia uma altura: no colar ela vinha da cadeia flex e
 * variava com o que mais estivesse na tela; na prévia do arquivo era uma medida
 * própria. Como é o MESMO campo, com duas origens de conteúdo, a caixa passa a
 * ter um tamanho só — e o botão de expandir cobre quem precisa de mais espaço.
 *
 * 14rem é o piso que o `.form-group--editor` já usava; fixá-lo apenas troca
 * "pelo menos isto" por "exatamente isto".
 */
const EDITOR_HEIGHT = "14rem";

// O .txt é lido no navegador; as demais precisam do servidor — ver
// processFiles. O .doc entra mesmo sem suporte real: é o servidor que recusa
// com a mensagem instruindo a salvar como .docx (mesmo motivo do AddTextForm).
const allowedExtensions = [".txt", ".pdf", ".epub", ".doc", ".docx"];

// Extensões cuja extração passa pelo servidor — mesmo conjunto do AddTextForm.
const serverExtractedExtensions = [".pdf", ".epub", ".doc", ".docx"];

// Dica do botão. Sem .doc de propósito — ele nunca importa de verdade, ver
// acima — e sem repetir a lista de extensões no rótulo, que é de largura fixa.
const IMPORT_HINT = "Formatos aceitos: .txt, .pdf, .epub, .docx";

// Extrair um PDF de livro leva segundos. Sem sinal na tela, parece travamento.
const isExtracting = ref(false);

/** Extensão em curso de extração, em maiúsculas ("PDF"), para o aviso na tela. */
const extractingExtension = ref("");

// ── Conversão para HTML disparada por uma importação ─────────────────────────
// EPUB/DOCX chegam como HTML de verdade. Se o texto sendo editado ainda é
// plaintext, o backend recusa pastedContentIsHtml nessa combinação (ver
// TextsController.EditText) — converter é ato deliberado, com ensaio e
// backup (ConvertTextToHtmlModal, mesmo componente do botão no leitor). O
// arquivo fica pendente até o usuário confirmar ali.
const showConvertModal = ref(false);
const pendingFileImport = ref(null); // { fileName, html } | null

/** Aplica o conteúdo importado — único ponto que os dois caminhos (direto e
 * pós-conversão) usam para terminar a importação da mesma forma. */
function applyImportedContent(name, html) {
  obj.value.pastedContent = html;
  fileName.value = name;
  fieldErrors.value.file = null;
  fieldErrors.value.pastedContent = null;
  inputMethod.value = "file";
}

async function processFiles(files) {
  const file = files[0];
  if (!file) return;
  const fileExtension = file.name
    .slice(file.name.lastIndexOf("."))
    .toLowerCase();
  if (!allowedExtensions.includes(fileExtension)) {
    showErrorToast("Formato de arquivo não permitido: " + file.name);
    return;
  }

  const isServerExtracted = serverExtractedExtensions.includes(fileExtension);
  if (isServerExtracted) {
    // Guardado à parte de `fileName`, que só é preenchido no SUCESSO: durante a
    // extração ele ainda está vazio (ou com o arquivo anterior), e o aviso
    // precisa dizer de qual formato se trata agora.
    extractingExtension.value = fileExtension.replace(".", "").toUpperCase();
    isExtracting.value = true;
  }

  try {
    let html;
    let isHtml = false;

    if (isServerExtracted) {
      // Nenhum desses o navegador lê sozinho: quem extrai é o servidor, e os
      // três devolvem HTML canônico, já normalizado. EPUB e DOCX já SÃO HTML
      // na origem; o PDF não é, mas o PdfHtmlExtractor lê do próprio arquivo a
      // estrutura que o texto puro não carrega — título, ênfase, lista,
      // alinhamento e recuo. Consequência para esta tela: importar PDF num
      // texto ainda em texto puro agora cai no modal de conversão logo abaixo,
      // como já acontecia com EPUB e DOCX. .doc não é suportado — o backend
      // recusa com mensagem própria (ver o catch abaixo), instruindo a salvar
      // como .docx.
      const response = (await extractTextFromFile(file)).data;
      isHtml = !!response.isHtml;
      html = isHtml ? response.text : unwrapPdfLineBreaks(response.text);
    } else {
      // Mesmo tratamento de quebra de linha "dura" de PDF que o editor já
      // aplica ao colar (unwrapPastedLineBreaks) — aqui aplicado manualmente,
      // já que carregar um arquivo não passa por um evento de colagem.
      html = unwrapPdfLineBreaks(await file.text());
    }

    if (isHtml && !isHtmlContent.value) {
      // Conteúdo HTML de verdade, texto ainda plaintext: fica pendente até a
      // conversão ser confirmada no modal — ver o bloco de estado acima.
      pendingFileImport.value = { fileName: file.name, html };
      showErrorToast(
        "Este texto ainda está em texto puro. Confirme a conversão para HTML abaixo para importar este arquivo."
      );
      showConvertModal.value = true;
      return;
    }

    // Neste ponto, isHtml só é true quando isHtmlContent também é — o outro
    // caso já retornou acima. Texto puro (hoje só o .txt, lido aqui mesmo)
    // ainda vira HTML pelas mesmas regras de bloco do PlaintextToHtml do
    // backend quando o texto editado já é HTML; texto puro para texto puro
    // fica como está — abrir o editor rich text aqui converteria
    // implicitamente, sem ensaio/backup.
    const content = isHtml
      ? html
      : isHtmlContent.value
      ? plaintextToEditorHtml(html)
      : html;

    applyImportedContent(file.name, content);
  } catch (e) {
    console.warn("Falha ao carregar o conteúdo do arquivo:", e);
    // A API tem o que dizer em vários casos — PDF sem OCR, arquivo grande
    // demais — e a mensagem dela é mais útil que a genérica.
    showErrorToast(
      e?.response?.data?.message ||
        "Não foi possível ler o conteúdo do arquivo selecionado."
    );
    return;
  } finally {
    isExtracting.value = false;
  }
}

/** Conversão confirmada no modal: o texto virou HTML no backend. */
async function handleFileImportConverted() {
  const pending = pendingFileImport.value;
  if (!pending) return;

  try {
    // Recarrega o texto: contentFormat virou "html", e é dele que
    // isHtmlContent deriva — sem isto o formulário continuaria achando que
    // ainda é plaintext, e o salvamento seria recusado de novo.
    await textStore.getTextById(props.textId);

    // Sobrepõe: o arquivo importado é o que o usuário quis trazer agora, não
    // o conteúdo recém-convertido do texto antigo — que só existiu para o
    // texto deixar de ser plaintext.
    applyImportedContent(pending.fileName, pending.html);
    pendingFileImport.value = null;
  } catch (e) {
    console.warn("Falha ao recarregar o texto após a conversão:", e);
    showErrorToast(
      "O texto foi convertido, mas não foi possível recarregá-lo aqui. Feche e reabra a edição para importar o arquivo."
    );
    // Não limpa pendingFileImport nem o input: se o modal fechar em seguida,
    // handleConvertModalClose trata como importação não concluída.
  }
}

/** Modal fechado — com ou sem conversão. */
function handleConvertModalClose() {
  showConvertModal.value = false;

  // Fechou sem confirmar (Cancelar, ou clique fora): a importação não
  // aconteceu, e o arquivo escolhido não deixa rastro no formulário.
  if (pendingFileImport.value) {
    pendingFileImport.value = null;
    if (fileInputRef.value) fileInputRef.value.value = "";
  }
}

function handleFile(event) {
  const files = event.target.files;
  if (files && files.length > 0) {
    processFiles(files);
  }
}

function addNewAuthor(newTag) {
  newAuthorName.value = toTitleCase(newTag);
  showAddAuthorModal.value = true;
}

function handleAuthorAdded(newAuthor) {
  authorList.value.push(newAuthor);
  selectedAuthor.value = [newAuthor];
  showAddAuthorModal.value = false;
}

function addNewCategory(newTag) {
  newCategoryName.value = toTitleCase(newTag);
  showAddCategoryModal.value = true;
}

function handleCategoryAdded(option) {
  categoryList.value.push(option);
  selectedCategory.value = [option];
  showAddCategoryModal.value = false;
}

function addNewTopic(newTag) {
  newTopicName.value = toTitleCase(newTag);
  showAddTopicModal.value = true;
}

function handleTopicAdded(option) {
  const normalized = toOptionWithSys({
    label: option.label,
    value: option.value,
    description: option.description,
    isSys: option.isSys || false,
  });
  topicList.value.push(normalized);
  selectedTopics.value.push(normalized);
  obj.value.topicIds = selectedTopics.value.map((t) => t.value);
  showAddTopicModal.value = false;
  focusTopicMultiselect();
}

async function salvarAlteracoes() {
  // Salvar no meio de uma extração gravaria o conteúdo antigo por cima do novo.
  if (isSubmitting.value || isExtracting.value) return;

  // Reseta os erros a cada tentativa de submissão
  fieldErrors.value = {};

  // Mesma regra do cadastro: título é o único campo de texto obrigatório.
  if (!(obj.value.title || "").trim()) {
    fieldErrors.value.title = "Por favor, informe o título do texto.";
    nextTick(() => titleFocusRef.value?.focus());
    return;
  }

  // Permissões gerenciadas pelo mini-modal
  isSubmitting.value = true;

  try {
    const dataToSend = {
      ...obj.value,
      authorId: obj.value.authorId ?? 0,
      categoryId: obj.value.categoryId ?? 0,
      topicIds: Array.isArray(obj.value.topicIds) ? obj.value.topicIds : [],
      // Capa: os dois campos so viajam quando ha o que dizer sobre ela.
      coverBlob: coverBlob.value,
      removeCover: removeCover.value,
      // O conteúdo vem sempre do editor — tanto o que foi digitado/colado
      // quanto o que veio de um arquivo carregado (convertido ao carregar,
      // ver processFiles), para o usuário revisar antes de salvar.
      pastedContent: contentToSend(),
      pastedContentIsHtml: isHtmlContent.value,
      file: null,
    };

    await textStore.updateText(dataToSend);

    finishSave("Alterações salvas com sucesso.");
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;

    // Conflito 409: Impacto de Notas
    if (status === 409 && data?.requiresConfirmation) {
      const impacted = data.impactedNotes || [];
      const onlyRangeShift =
        impacted.length > 0 &&
        impacted.every((n) => n.impactType === "RangeShiftOnly");

      const basePayload = {
        ...obj.value,
        authorId: obj.value.authorId ?? 0,
        categoryId: obj.value.categoryId ?? 0,
        topicIds: Array.isArray(obj.value.topicIds) ? obj.value.topicIds : [],
        // Capa: os dois campos so viajam quando ha o que dizer sobre ela.
        coverBlob: coverBlob.value,
        removeCover: removeCover.value,
        file: null,
        pastedContent: contentToSend(),
        pastedContentIsHtml: isHtmlContent.value,
      };

      if (onlyRangeShift) {
        try {
          const payloadToSend = { ...basePayload, forceApply: true };
          await textStore.updateText(payloadToSend);
          finishSave("Alterações salvas com sucesso.");
          return;
        } catch (err2) {
          showErrorToast(
            err2.response?.data?.message ||
              err2.message ||
              "Não foi possível aplicar as alterações."
          );
          isSubmitting.value = false;
          return;
        }
      }

      impactResult.value = data;
      pendingUpdatePayload.value = basePayload;

      const decisions = {};
      (data.impactedNotes || [])
        .filter((n) => n.impactType !== "RangeShiftOnly")
        .forEach((n) => {
          decisions[n.noteId] = true;
        });
      noteDecisions.value = decisions;
      showImpactModal.value = true;
      isSubmitting.value = false;
      return;
    }

    // Erros de Validação da API
    if (data?.errors && typeof data.errors === "object") {
      let hasApiFieldErrors = false;
      for (const field in data.errors) {
        const fieldErrorsArray = data.errors[field];
        if (Array.isArray(fieldErrorsArray) && fieldErrorsArray.length > 0) {
          let frontendField = field.charAt(0).toLowerCase() + field.slice(1);
          
          // Compatibilidade com o backend C# (TopicsId -> topicIds)
          if (frontendField === "topicsId") {
            frontendField = "topicIds";
          }

          fieldErrors.value[frontendField] = fieldErrorsArray[0];
          hasApiFieldErrors = true;
        }
      }

      if (hasApiFieldErrors) {
        isSubmitting.value = false;
        // Erro num campo que mora na seção recolhida: abre a seção, senão a
        // mensagem inline ficaria escondida.
        if (
          fieldErrors.value.subtitle ||
          fieldErrors.value.synopsis ||
          fieldErrors.value.source ||
          fieldErrors.value.authorId ||
          fieldErrors.value.categoryId ||
          fieldErrors.value.topicIds
        ) {
          showDetails.value = true;
        }
        return; // Interrompe para exibir os erros inline
      }
    }

    // Tratamento de erros genéricos ou de limite de plano
    let userFriendlyMessage = "Ocorreu um erro desconhecido ao salvar as alterações.";
    
    if (data?.message) {
      userFriendlyMessage = data.message;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API ao salvar:", data || error);

    let isLimitError = false;
    if (
      typeof userFriendlyMessage === "string" &&
      userFriendlyMessage.includes("Seu plano atual permite")
    ) {
      isLimitError = true;
      textLimitInfo.value = {
        currentUsage: null,
        limit: null,
        newUsage: null,
        message: userFriendlyMessage,
      };
    }

    if (isLimitError) {
      showTextLimitModal.value = true;
    } else {
      showErrorToast(userFriendlyMessage);
    }
    isSubmitting.value = false;
  }
}

function closeImpactModal() {
  showImpactModal.value = false;
}

// Confirmar modal de impacto
async function confirmApplyTextEdit() {
  if (!pendingUpdatePayload.value) {
    showImpactModal.value = false;
    return;
  }

  // Reseta os erros
  fieldErrors.value = {};

  try {
    isSubmitting.value = true;

    const detachNoteIds = [];
    const deleteNoteIds = [];

    for (const [idStr, keep] of Object.entries(noteDecisions.value)) {
      const id = Number(idStr);
      if (keep === false) deleteNoteIds.push(id);
      else detachNoteIds.push(id);
    }

    const payloadToSend = {
      ...pendingUpdatePayload.value,
      detachNoteIds,
      deleteNoteIds,
      acceptedNoteLossIds: deleteNoteIds,
      forceApply: true,
    };

    await textStore.updateText(payloadToSend);

    showImpactModal.value = false;
    finishSave("Alterações aplicadas.");
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;

    let userFriendlyMessage = "Não foi possível aplicar as alterações mesmo após a confirmação.";

    // Validação API no Apply
    if (data?.errors && typeof data.errors === "object") {
      let hasApiFieldErrors = false;
      for (const field in data.errors) {
        const fieldErrorsArray = data.errors[field];
        if (Array.isArray(fieldErrorsArray) && fieldErrorsArray.length > 0) {
          let frontendField = field.charAt(0).toLowerCase() + field.slice(1);
          if (frontendField === "topicsId") {
            frontendField = "topicIds";
          }
          fieldErrors.value[frontendField] = fieldErrorsArray[0];
          hasApiFieldErrors = true;
        }
      }

      if (hasApiFieldErrors) {
        showImpactModal.value = false; // Fecha o modal de impacto para mostrar os erros inline
        isSubmitting.value = false;
        // Erro num campo que mora na seção recolhida: abre a seção, senão a
        // mensagem inline ficaria escondida.
        if (
          fieldErrors.value.subtitle ||
          fieldErrors.value.synopsis ||
          fieldErrors.value.source ||
          fieldErrors.value.authorId ||
          fieldErrors.value.categoryId ||
          fieldErrors.value.topicIds
        ) {
          showDetails.value = true;
        }
        return; 
      }
    } else if (data?.message) {
      userFriendlyMessage = data.message;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    if (
      typeof userFriendlyMessage === "string" &&
      userFriendlyMessage.includes("Seu plano atual permite")
    ) {
      showImpactModal.value = false;
      textLimitInfo.value = {
        currentUsage: null,
        limit: null,
        newUsage: null,
        message: userFriendlyMessage,
      };
      showTextLimitModal.value = true;
      isSubmitting.value = false;
      return;
    }

    if (status === 409 && data?.requiresConfirmation) {
      impactResult.value = data;
      showImpactModal.value = true;
      isSubmitting.value = false;
      return;
    }

    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

// ESC / Enter
const handleEnterKey = (event) => {
  if (event.key !== "Enter") return;
  // Na confirmação não há mais nada para submeter.
  if (savedText.value) return;
  if (
    showAddAuthorModal.value ||
    showAddCategoryModal.value ||
    showAddTopicModal.value ||
    showPlanCatalogModal.value ||
    showTextLimitModal.value ||
    showImpactModal.value ||
    showTopicCardModal.value ||
    showAuthorCardModal.value ||
    showCategoryCardModal.value ||
    showPermissionModal.value
  ) {
    return;
  }

  const el = event.target;
  const tag = (el?.tagName || "").toLowerCase();
  const isEditable = !!el?.isContentEditable;

  if (tag === "textarea" || isEditable) return;
  if (tag === "button" || tag === "a") return;

  // Dentro de um Multiselect o Enter é DELE: seleciona a opção destacada ou
  // cria o item novo ("tecle enter para criar"). O campo de busca é um <input>
  // comum, então sem esta guarda ele caía na regra geral acima e o Enter
  // salvava o formulário em vez de criar o autor, gênero ou assunto digitado.
  if (typeof el?.closest === "function" && el.closest(".multiselect")) return;

  event.preventDefault();
  salvarAlteracoes();
};

onMounted(async () => {
  await Promise.all([
    userStore.fetchUsers(1, 1000, ""),
    groupStore.fetchGroups(1, 1000, ""),
    permissionStore.fetchAllPermissions(),
    authorStore.fetchAuthors(),
    categoryStore.fetchCategories(),
    topicStore.fetchTopics(),
  ]);

  authorList.value = authorStore.authors.map((a) => ({
    label: a.authorName,
    value: a.authorId,
    isSys: isSysEntity(a),
  }));

  categoryList.value = categoryStore.categories.map((c) => ({
    label: c.categoryName,
    value: c.categoryId,
    isSys: isSysEntity(c),
  }));

  topicList.value = topicStore.topics.map((t) =>
    toOptionWithSys({
      label: t.topicName,
      value: t.topicId,
      description: t.description,
      isSys: isSysEntity(t),
    })
  );

  isLoading.value = true;
  await carregarDados();
  isLoading.value = false;

  document.addEventListener("keydown", handleEnterKey);
  document.addEventListener("click", handleVisibilityClickOutside);

  // Sem campo pedido, o foco é o de sempre: o título.
  if (props.focusField) focusCatalogField(props.focusField);
  else nextTick(() => titleFocusRef.value?.focus());
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEnterKey);
  document.removeEventListener("click", handleVisibilityClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

// Watchers para limpar erros e atualizar estados
watch(selectedAuthor, (val) => {
  // Seleção única: ao escolher um novo autor, substitui o anterior automaticamente
  if (Array.isArray(val) && val.length > 1) {
    selectedAuthor.value = [val[val.length - 1]];
    return;
  }
  fieldErrors.value.authorId = null;
  const first = Array.isArray(val) ? val[0] : val;
  obj.value.authorId = first?.value || 0;
});

watch(selectedCategory, (val) => {
  // Seleção única: ao escolher um novo gênero, substitui o anterior automaticamente
  if (Array.isArray(val) && val.length > 1) {
    selectedCategory.value = [val[val.length - 1]];
    return;
  }
  fieldErrors.value.categoryId = null;
  const first = Array.isArray(val) ? val[0] : val;
  obj.value.categoryId = first?.value || 0;
});

watch(selectedTopics, (val) => {
  fieldErrors.value.topicIds = null;
  obj.value.topicIds = Array.isArray(val) ? val.map((x) => x.value) : [];
});

watch(
  () => showImpactModal.value,
  (isOpen) => {
    if (isOpen) {
      nextTick(() => {
        const el = document.getElementById("impact-modal-wrapper");
        el?.focus();
      });
    }
  }
);
</script>

<template>
  <div v-show="!hideBackdropForSuccess" class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <div class="modal-header-text">
          <h3 class="modal-title">Editar Texto</h3>
          <p class="modal-step-indicator">
            Edite o que precisar, o resto fica como está
          </p>
        </div>
        <button @click="closeModal" type="button" class="modal-close-btn">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="salvarAlteracoes" class="modal-form">
        <div
          class="modal-body"
          :class="{ 'modal-body--expanded': isBodyExpanded }"
        >

          <!-- ✅ Overlay de carregamento -->
          <transition name="loading-fade">
            <div v-if="isLoading" class="loading-overlay" aria-label="Carregando...">
              <svg class="loading-spinner" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
              </svg>
              <span class="loading-text">Carregando...</span>
            </div>
          </transition>

          <!-- Uma tela só, na mesma hierarquia do cadastro: título, conteúdo,
               visibilidade — e a catalogação recolhida em "Mais informações". -->
          <div
            v-if="!savedText"
            class="step-content"
            :class="{ 'step-content--expanded': isBodyExpanded }"
          >
            <!-- 1. Título — o único campo de texto obrigatório -->
            <div class="form-group" v-show="!isBodyExpanded">
              <label for="title" class="form-label">Título</label>
              <input
                ref="titleFocusRef"
                v-model="obj.title"
                @input="fieldErrors.title = null"
                @blur="obj.title = toTitleCase(obj.title)"
                type="text"
                id="title"
                class="input-base"
                :class="{ 'input-error': fieldErrors.title }"
                :disabled="isLoading"
                placeholder="Digite o título do texto"
                required
              />
              <span v-if="fieldErrors.title" class="error-msg">{{ fieldErrors.title }}</span>
            </div>

            <!-- Conteúdo — mesma apresentação do cadastro rápido: os dois modos
                 em destaque e o editor logo abaixo. -->
            <div class="form-group" v-show="!isBodyExpanded">
              <!-- Dois campos lado a lado: cada um com seu rótulo em cima e seu
                   controle embaixo. Os rótulos dividem uma linha e os controles
                   dividem a seguinte, e as duas colunas se alinham porque a da
                   direita tem largura fixa (--visibility-col). -->
              <div class="content-label-row">
                <!-- "Forma de entrada", e não "Conteúdo": este rótulo governa os
                     botões de método, não a caixa de texto. Quem rotula o
                     conteúdo é a linha logo acima do editor. -->
                <label class="form-label">Forma de entrada</label>
                <span class="form-label visibility-col" id="visibilityLabel"
                  >Visibilidade</span
                >
              </div>

              <div class="content-method-row">
                <div class="access-segment access-segment--sm">
                  <button
                    type="button"
                    class="segment-btn segment-btn--neutral"
                    :class="{ 'segment-btn--active': inputMethod === 'paste' }"
                    :disabled="isLoading || isExtracting"
                    @click="setInputMethod('paste')"
                  >
                    <i class="fas fa-clipboard segment-btn__icon"></i>
                    <span class="segment-btn__label">Colar/Editar Texto</span>
                  </button>

                  <button
                    type="button"
                    class="segment-btn segment-btn--neutral"
                    :class="{ 'segment-btn--active': inputMethod === 'file' }"
                    :disabled="isLoading || isExtracting"
                    :title="IMPORT_HINT"
                    @click="setInputMethod('file')"
                  >
                    <i class="fas fa-file-upload segment-btn__icon"></i>
                    <span class="segment-btn__label">Importar arquivo</span>
                  </button>
                </div>

                <div class="visibility-inline visibility-col">
                  <div ref="visibilityDropdownRef" class="visibility-dropdown">
                    <button
                      type="button"
                      class="visibility-trigger"
                      :class="{ active: isVisibilityMenuOpen }"
                      :disabled="isLoading || isSubmitting"
                      aria-labelledby="visibilityLabel"
                      :aria-expanded="isVisibilityMenuOpen"
                      @click.stop="toggleVisibilityMenu"
                    >
                      <i
                        :class="[
 'fas',
                          visibilityMeta(currentAccessMode).icon,
                          'visibility-trigger__icon',
                          visibilityMeta(currentAccessMode).colorClass,
                        ]"
                      ></i>
                      <span class="visibility-trigger__label">{{
                        visibilityMeta(currentAccessMode).label
                      }}</span>
                      <i
                        class="fas fa-chevron-down visibility-trigger__chevron"
                        :class="{ open: isVisibilityMenuOpen }"
                      ></i>
                    </button>

                    <div v-if="isVisibilityMenuOpen" class="visibility-menu">
                      <ul>
                        <li
                          v-for="opt in VISIBILITY_OPTIONS"
                          :key="opt.value"
                          class="visibility-option"
                          :class="[
 opt.colorClass,
                            {
                              'visibility-option--current':
                                currentAccessMode === opt.value,
                            },
                          ]"
                          :title="opt.hint"
                          @click="handleVisibilityOption(opt.value)"
                        >
                          <span class="visibility-option__label">
                            <i
                              :class="[
 'fas',
                                opt.icon,
                                'visibility-option__icon',
                              ]"
                            ></i>
                            {{ opt.label }}
                          </span>
                          <i
                            v-if="currentAccessMode === opt.value"
                            class="fas fa-check visibility-option__check"
                          ></i>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Só no modo personalizado: é o único em que ainda há algo a
                       configurar depois de escolher. -->
                  <button
                    v-if="currentAccessMode === 'custom'"
                    type="button"
                    class="access-hint__link"
                    :disabled="isLoading || isSubmitting"
                    @click="openPermissionModal"
                  >
                    Editar permissões
                  </button>
                </div>
              </div>
            </div>

            <!--
              O input fica fora dos dois ramos de modo porque quem o aciona é o
              botão do segmento, que também está fora deles. Escondido: a caixa
              que o navegador desenha não combina com o resto do formulário.

              A linha de estado que existia aqui SAIU. Ela ficava em branco quase
              o tempo todo — só tinha o que dizer durante a extração — e essa
              faixa vazia era o vão entre "Forma de entrada" e o rótulo do
              editor. O aviso de extração passou para dentro do próprio rótulo,
              que já existe em todo estado: some o vão, e continua não havendo
              solavanco, porque nada nasce nem morre no fluxo.
            -->
            <input
              ref="fileInputRef"
              id="dropzone-file"
              type="file"
              class="hidden"
              :disabled="isExtracting"
              @change="handleFile"
              @cancel="handleFilePickerCancel"
              accept=".txt,.pdf,.epub,.doc,.docx"
            />

            <div
              v-if="inputMethod === 'paste'"
              class="form-group form-group--editor"
            >
              <!-- A MESMA linha existe nos dois modos, acima do editor: rótulo à
                   esquerda, expandir à direita. É o que faz colar e carregar
                   lerem como o mesmo campo com duas origens, em vez de duas
                   telas diferentes. -->
              <div class="body-label-row">
                <label class="form-label">Conteúdo</label>

                <!-- 🔹 NOVO: botão expandir/colapsar (espelho do AddNoteForm) -->
                <button
                  type="button"
                  class="ct-expand-btn-inline"
                  :disabled="isLoading || isSubmitting"
                  @click="handleToggleBodyExpand"
                  :aria-label="
                    isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                  "
                  :title="
                    isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                  "
                >
                  <i
                    v-if="!isBodyExpanded"
                    class="fa fa-expand"
                    aria-hidden="true"
                  ></i>
                  <i v-else class="fa fa-compress" aria-hidden="true"></i>
                </button>
              </div>

              <!--
                Dois editores, um por formato. Texto já em HTML ganha o editor
                formatado; texto puro continua na caixa de texto — abrir o editor
                rich text sobre conteúdo plano converteria o texto sem ensaio, sem
                backup e sem ninguém conferir os offsets das notas.
              -->
              <div class="editor-container">
                <!--
                  Altura FIXA, a mesma em todos os modos e igual à do AddTextForm:
                  piso e teto iguais fecham a caixa e entregam o excesso à rolagem
                  interna do `.rte-content`. Antes ela vinha da cadeia flex, e por
                  isso mudava conforme o que mais estivesse na tela.

                  Expandido é a exceção, e é a razão de o botão existir: ali o
                  piso volta a zero e o teto sai, e a altura passa a vir da cadeia
                  flex de novo.
                -->
                <RichTextEditor
                  :disabled="isExtracting"
                  v-if="isHtmlContent"
                  v-model="obj.pastedContent"
                  @update:modelValue="fieldErrors.pastedContent = null"
                  class="editor-textarea"
                  :class="{ 'textarea-error': fieldErrors.pastedContent }"
                  :unwrapPastedLineBreaks="true"
                  :min-height="isBodyExpanded ? '0' : EDITOR_HEIGHT"
                  :max-height="isBodyExpanded ? null : EDITOR_HEIGHT"
                  placeholder="Edite o conteúdo do texto…"
                />
                <CustomTextarea
                  :disabled="isExtracting"
                  v-else
                  v-model:body="obj.pastedContent"
                  @update:body="fieldErrors.pastedContent = null"
                  class="editor-textarea"
                  :class="{ 'textarea-error': fieldErrors.pastedContent }"
                  :unwrapPastedLineBreaks="true"
                />
              </div>
              <span v-if="fieldErrors.pastedContent" class="error-msg">{{ fieldErrors.pastedContent }}</span>
            </div>

            <!--
              Sem zona de arrasto: escolher "Importar arquivo" já abre o seletor
              nativo (ver setInputMethod). O que sobra aqui é a linha de estado —
              qual arquivo está carregado e como trocá-lo — que é uma linha só,
              contra as seis que a zona ocupava.
            -->
            <div v-else class="form-group form-group--file">
              <span v-if="fieldErrors.file" class="error-msg">{{ fieldErrors.file }}</span>

              <!-- Conteúdo do arquivo — mesmo editor do modo "Colar/Editar Texto"
                   (rich text se o texto já é HTML, caixa simples se ainda é
                   plaintext), para o usuário conferir e ajustar antes de salvar.

                   Sempre montado, e não só depois de carregar: com `v-if` a caixa
                   nascia e sumia, e o formulário saltava de altura a cada arquivo
                   escolhido. Vazio ele é o destino visível do que vai ser
                   carregado; e num texto que já tem conteúdo, mostra o que está
                   prestes a ser substituído — que é informação, não ruído.

                   O rótulo é que muda de papel conforme o estado. -->
              <div class="loaded-file-preview">
                <!-- A MESMA linha do modo colar: rótulo à esquerda, expandir à
                     direita. O rótulo muda de palavras conforme o estado, mas o
                     arranjo não muda — é o que faz os dois modos lerem como o
                     mesmo campo. -->
                <div class="body-label-row">
                  <!-- Um rótulo, três estados. O nome do arquivo entra entre
                       parênteses porque qualifica o conteúdo que está na caixa
                       logo abaixo; o aviso de extração ocupa o mesmo lugar em vez
                       de uma linha própria, que ficaria vazia o resto do tempo.
                       `:title` carrega o nome inteiro quando as reticências
                       cortam. -->
                  <label
                    for="fileContentEditor"
                    class="form-label body-label-row__label"
                    :class="{ 'body-label-row__label--busy': isExtracting }"
                    :title="fileName || undefined"
                  >
                    <i
                      v-if="isExtracting"
                      class="fas fa-spinner fa-spin body-label-row__spinner"
                    ></i>
                    <template v-if="isExtracting"
                      >Extraindo conteúdo do {{ extractingExtension }}. Isso pode
                      levar vários segundos...</template
                    >
                    <template v-else-if="fileName"
                      >Conteúdo carregado
                      (<span class="body-label-row__file">{{ fileName }}</span
                      >)</template
                    >
                    <template v-else>Conteúdo</template>
                  </label>

                  <div class="body-label-row__actions">
                    <button
                      type="button"
                      class="ct-expand-btn-inline"
                      :disabled="isLoading || isSubmitting"
                      @click="handleToggleBodyExpand"
                      :aria-label="
                        isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                      "
                      :title="
                        isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                      "
                    >
                      <i
                        v-if="!isBodyExpanded"
                        class="fa fa-expand"
                        aria-hidden="true"
                      ></i>
                      <i v-else class="fa fa-compress" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <!-- Mesma altura fixa do modo colar — ver EDITOR_HEIGHT. -->
                <RichTextEditor
                  :disabled="isExtracting"
                  v-if="isHtmlContent"
                  v-model="obj.pastedContent"
                  @update:modelValue="fieldErrors.pastedContent = null"
                  :unwrapPastedLineBreaks="true"
                  :min-height="isBodyExpanded ? '0' : EDITOR_HEIGHT"
                  :max-height="isBodyExpanded ? null : EDITOR_HEIGHT"
                  placeholder="Conteúdo do arquivo…"
                />
                <!-- Texto ainda em plaintext: caixa simples, mas com a MESMA
                     altura útil do editor colapsado ao lado — ver
                     .file-preview-textarea. Sem isso o `min-height: 200px` do
                     componente fazia este formulário abrir bem mais alto que o
                     de cadastro, para o mesmo arquivo. -->
                <CustomTextarea
                  :disabled="isExtracting"
                  v-else
                  v-model:body="obj.pastedContent"
                  @update:body="fieldErrors.pastedContent = null"
                  :unwrapPastedLineBreaks="true"
                  class="file-preview-textarea"
                  placeholder="Conteúdo do arquivo…"
                />
              </div>
            </div>

            <!-- A visibilidade subiu para a linha do campo Conteúdo; aqui resta
                 o acesso ao resto da catalogação. -->
            <div class="quick-row" v-show="!isBodyExpanded">
              <!-- O resto da catalogação, discreto e recolhido -->
              <button
                type="button"
                class="details-toggle"
                :class="{ 'details-toggle--open': showDetails }"
                :disabled="isLoading || isSubmitting"
                :aria-expanded="showDetails"
                @click="toggleDetails"
              >
                <i
                  class="fas fa-chevron-down details-toggle__chevron"
                  aria-hidden="true"
                ></i>
                <span>Mais informações</span>
              </button>
            </div>

            <div
              ref="detailsPanelRef"
              v-show="showDetails && !isBodyExpanded"
              class="details-panel"
            >
              <p class="details-panel__hint">
                <i class="fas fa-info-circle"></i>
                Subtítulo, fonte, autor, gênero e assuntos — nenhum deles
                obrigatório para salvar.
              </p>

              <div class="form-group-toggle">
                <label for="active" class="form-label">Texto Ativo</label>
                <label class="toggle-switch">
                  <input v-model="obj.active" type="checkbox" id="active" :disabled="isLoading" />
                  <span class="slider"></span>
                </label>
              </div>

              <div class="form-group">
                <label for="subtitle" class="form-label">Subtítulo (opcional)</label>
                <input
                  v-model="obj.subtitle"
                  @input="fieldErrors.subtitle = null"
                  @blur="obj.subtitle = toTitleCase(obj.subtitle)"
                  type="text"
                  id="subtitle"
                  class="input-base"
                  :class="{ 'input-error': fieldErrors.subtitle }"
                  :disabled="isLoading"
                  placeholder="Digite o subtítulo"
                />
                <span v-if="fieldErrors.subtitle" class="error-msg">{{ fieldErrors.subtitle }}</span>
              </div>

              <div class="form-group">
                <label for="source" class="form-label">Fonte (opcional)</label>
                <input
                  v-model="obj.source"
                  @input="fieldErrors.source = null"
                  @blur="obj.source = toTitleCase(obj.source)"
                  type="text"
                  id="source"
                  class="input-base"
                  :class="{ 'input-error': fieldErrors.source }"
                  :disabled="isLoading"
                  placeholder="Digite a fonte do texto"
                />
                <span v-if="fieldErrors.source" class="error-msg">{{ fieldErrors.source }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">Autor (opcional)</label>
                <Multiselect
                  ref="authorFocusRef"
                  v-model="selectedAuthor"
                  :options="authorList"
                  :class="{ 'ms-error': fieldErrors.authorId }"
                  placeholder="Selecione ou crie um autor"
                  label="label"
                  track-by="value"
                  :allow-empty="true"
                  :taggable="true"
                  :multiple="true"
                  open-direction="above"
                  tag-placeholder="Tecle enter ou clique aqui para criar um autor"
                  select-label="Tecle enter para selecionar"
                  :close-on-select="true"
                  :clear-on-select="true"
                  :disabled="isLoading"
                  @tag="addNewAuthor"
                  @search-change="onSearch"
                >
                  <template #tag="{ option, remove }">
                    <span class="ms-chip">
                      <button
                        type="button"
                        class="ms-chip-link"
                        @click.stop="openAuthorCard(option)"
                      >
                        {{ option.label }}
                      </button>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                      <button
                        type="button"
                        class="ms-chip-remove"
                        @click.stop="remove(option)"
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    </span>
                  </template>

                  <template #option="{ option }">
                    <div class="ms-option">
                      <span class="ms-label">{{ option.label }}</span>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                    </div>
                  </template>
                </Multiselect>
                <span v-if="fieldErrors.authorId" class="error-msg">{{ fieldErrors.authorId }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">Gênero (opcional)</label>
                <Multiselect
                  ref="categoryFocusRef"
                  v-model="selectedCategory"
                  :options="categoryList"
                  :class="{ 'ms-error': fieldErrors.categoryId }"
                  placeholder="Selecione ou crie um gênero"
                  label="label"
                  track-by="value"
                  :allow-empty="true"
                  :taggable="true"
                  :multiple="true"
                  open-direction="above"
                  tag-placeholder="Tecle enter ou clique aqui para criar um gênero"
                  select-label="Tecle enter para selecionar"
                  :close-on-select="true"
                  :clear-on-select="true"
                  :disabled="isLoading"
                  @tag="addNewCategory"
                >
                  <template #tag="{ option, remove }">
                    <span class="ms-chip">
                      <button
                        type="button"
                        class="ms-chip-link"
                        @click.stop="openCategoryCard(option)"
                      >
                        {{ option.label }}
                      </button>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                      <button
                        type="button"
                        class="ms-chip-remove"
                        @click.stop="remove(option)"
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    </span>
                  </template>

                  <template #option="{ option }">
                    <div class="ms-option">
                      <span class="ms-label">{{ option.label }}</span>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                    </div>
                  </template>
                </Multiselect>
                <span v-if="fieldErrors.categoryId" class="error-msg">{{ fieldErrors.categoryId }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">Assuntos (opcional)</label>
                <Multiselect
                  ref="topicFocusRef"
                  v-model="selectedTopics"
                  :options="topicList"
                  :class="{ 'ms-error': fieldErrors.topicIds }"
                  placeholder="Selecione ou crie assuntos"
                  label="label"
                  track-by="value"
                  :multiple="true"
                  open-direction="above"
                  tag-placeholder="Tecle enter ou clique aqui para criar um assunto"
                  select-label="Tecle enter para selecionar"
                  deselect-label="Tecle enter para remover"
                  selected-label="Selecionado"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :taggable="true"
                  :disabled="isLoading"
                  @tag="addNewTopic"
                >
                  <template #tag="{ option, remove }">
                    <span class="ms-chip">
                      <button
                        type="button"
                        class="ms-chip-link"
                        @click.stop="openTopicCard(option)"
                      >
                        {{ option.label }}
                      </button>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                      <button
                        type="button"
                        class="ms-chip-remove"
                        @click.stop="remove(option)"
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    </span>
                  </template>

                  <template #option="{ option }">
                    <span>
                      {{ option.label }}
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                    </span>
                  </template>
                </Multiselect>
                <span v-if="fieldErrors.topicIds" class="error-msg">{{ fieldErrors.topicIds }}</span>
              </div>

              <!-- Capa (opcional). Sem imagem, o card desenha a capa gerada —
                   por isso não há estado vazio a resolver aqui. -->
              <div class="form-group">
                <label class="form-label">Capa (opcional)</label>
                <div class="cover-picker">
                  <div class="cover-preview">
                    <img
                      v-if="coverThumbUrl"
                      :src="coverThumbUrl"
                      alt="Pré-visualização da capa"
                    />
                    <span v-else class="cover-preview__empty">
                      <i class="fas fa-image"></i>
                    </span>
                  </div>

                  <div class="cover-picker__actions">
                    <label class="btn-cover" :class="{ 'btn-cover--busy': isProcessingCover }">
                      <i class="fas fa-upload"></i>
                      <span>{{
                        isProcessingCover
                          ? "Processando…"
                          : coverThumbUrl
                          ? "Trocar imagem"
                          : "Escolher imagem"
                      }}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        class="cover-input"
                        :disabled="isLoading || isProcessingCover"
                        @change="onCoverChange"
                      />
                    </label>

                    <button
                      v-if="coverThumbUrl"
                      type="button"
                      class="btn-cover btn-cover--ghost"
                      :disabled="isLoading"
                      @click="clearCover"
                    >
                      <i class="fas fa-times"></i>
                      <span>Remover</span>
                    </button>

                    <p class="cover-hint">
                      A imagem é recortada em 3:4 e reduzida a 400&nbsp;px antes
                      do envio.
                    </p>
                    <p v-if="removeCover" class="cover-hint cover-hint--warn">
                      A capa será removida ao salvar.
                    </p>
                  </div>
                </div>
                <span v-if="coverError" class="error-msg">{{ coverError }}</span>
              </div>

              <!-- Último campo do painel: é o mais alto e o único de texto
                   livre longo — acima dos demais, empurraria a catalogação
                   inteira para fora da vista ao ser preenchido. -->
              <div class="form-group">
                <label for="synopsis" class="form-label">Sinopse (opcional)</label>
                <!-- Textarea, não input: a sinopse é um parágrafo. O maxlength
                     é o tamanho da coluna no banco — barrar aqui evita a viagem
                     até a API só para receber erro de tamanho. Sem toTitleCase
                     no blur: é frase, não nome catalogado. -->
                <textarea
                  v-model="obj.synopsis"
                  @input="fieldErrors.synopsis = null"
                  id="synopsis"
                  class="input-base synopsis-input"
                  :class="{ 'input-error': fieldErrors.synopsis }"
                  :disabled="isLoading"
                  rows="10"
                  :maxlength="SYNOPSIS_MAX_LENGTH"
                  placeholder="Um resumo curto, exibido no card do texto"
                ></textarea>
                <div class="synopsis-footer">
                  <span v-if="fieldErrors.synopsis" class="error-msg">{{ fieldErrors.synopsis }}</span>
                  <span v-else></span>
                  <!-- Só acende perto do teto: um contador sempre destacado
                       sugere que o limite é assunto do usuário desde o
                       primeiro caractere, e ele não é. -->
                  <span
                    class="synopsis-counter"
                    :class="{ 'synopsis-counter--near': synopsisNearLimit }"
                  >
                    {{ synopsisLength }}/{{ SYNOPSIS_MAX_LENGTH }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Confirmação: o texto foi salvo, mas a catalogação continua
               incompleta — os campos que faltam ficam à mão, cada um abrindo o
               formulário de volta com o cursor nele. -->
          <div v-else class="success-state">
            <div class="success-state__icon">
              <i class="fas fa-check" aria-hidden="true"></i>
            </div>
            <h4 class="success-state__title">{{ savedText.message }}</h4>
            <p class="success-state__text">
              “{{ savedText.title }}” foi atualizado.
            </p>

            <p class="success-state__pending-label">Ainda em aberto</p>

            <div class="success-pending">
              <button
                v-for="item in savedText.pending"
                :key="item.field"
                type="button"
                class="pending-chip"
                :title="`Preencher ${item.label} agora`"
                @click="resumeEditing(item.field)"
              >
                <i :class="['fas', item.icon, 'pending-chip__icon']"></i>
                <span class="pending-chip__label">{{ item.label }}</span>
                <i
                  class="fas fa-arrow-right pending-chip__go"
                  aria-hidden="true"
                ></i>
              </button>
            </div>

            <p class="success-state__hint">
              Clique em um deles para preencher agora, ou conclua e deixe para
              depois.
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <!-- Uma ação só: completar é papel dos itens em aberto acima, que
               devolvem o formulário já no campo certo. -->
          <template v-if="savedText">
            <button
              ref="finishFocusRef"
              type="button"
              class="btn btn-primary"
              @click="finishAndClose"
            >
              Concluir
            </button>
          </template>

          <template v-else>
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              Cancelar
            </button>

            <button
              type="submit"
              class="btn btn-success"
              :disabled="
                isLoading || isSubmitting || isExtracting || isBodyExpanded
              "
            >
              <svg
                v-if="isSubmitting"
                class="mr-1 -ml-1 w-5 h-5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>

              <svg
                v-else
                class="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                />
              </svg>

              <span>
                {{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}
              </span>
            </button>
          </template>
        </div>
      </form>
    </div>
  </div>

  <!-- ✅ Mini-modal de permissões (espelho de EditActivityForm.vue) -->
  <Teleport to="body">
    <transition name="permission-modal-fade">
      <div v-if="showPermissionModal" class="permission-modal-backdrop">
        <div
          ref="permPanelRef"
          class="permission-modal-panel"
          :style="permPanelStyle"
          @click.stop
        >
          <div
            class="permission-modal-header"
            :class="{
 'permission-modal-header--draggable': isPermDraggable,
              'permission-modal-header--dragging': isPermDragging,
            }"
            @pointerdown="onPermHandlePointerDown"
          >
            <h4 class="permission-modal-title">Editar Permissões</h4>
            <button
              type="button"
              class="permission-modal-close"
              @click="cancelPermissionModal"
              aria-label="Fechar Editar Permissões"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>

          <div class="permission-modal-body">
            <p class="permission-modal-instruction">
              Defina aqui usuários, grupos e permissões personalizadas para este texto.
            </p>
            <AddPermissionForm
              ref="permissionFormRef"
              v-model:payload="obj.permissions"
              :key="permissionFormKey"
              :users="userListForChild"
              :groups="groupListForChild"
              :permissions="permissionListForChild"
              :entityId="props.textId"
              entityType="Text"
              :creatorUserName="creatorUserName"
            />
          </div>

          <div class="permission-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelPermissionModal"
              :disabled="isSubmitting"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="confirmPermissionModal"
              :disabled="isSubmitting"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <AddTextLimitModal
    :show="showTextLimitModal"
    :info="textLimitInfo"
    entityType="textos"
    @close="showTextLimitModal = false"
    @open-plans="openPlansFromLimitModal"
  />

  <Teleport to="body">
    <div v-if="showAddAuthorModal" class="fixed inset-0 z-[10050]">
      <AddAuthorForm
        :initialName="newAuthorName"
        @close="showAddAuthorModal = false"
        @author-added="handleAuthorAdded"
      />
    </div>

    <div v-if="showAddCategoryModal" class="fixed inset-0 z-[10050]">
      <AddCategoryForm
        :initialName="newCategoryName"
        @category-added="handleCategoryAdded"
        @close="showAddCategoryModal = false"
      />
    </div>

    <div v-if="showAddTopicModal" class="fixed inset-0 z-[10050]">
      <AddTopicForm
        :initialName="newTopicName"
        @topic-added="handleTopicAdded"
        @close="showAddTopicModal = false"
      />
    </div>

    <AuthorCardModal
      v-if="showAuthorCardModal"
      :show="showAuthorCardModal"
      :authorId="selectedAuthorIdForModal"
      @close="closeAuthorCard"
    />

    <CategoryCardModal
      v-if="showCategoryCardModal"
      :show="showCategoryCardModal"
      :categoryId="selectedCategoryIdForModal"
      @close="closeCategoryCard"
    />

    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>

    <transition name="toast-fade">
      <div
        id="impact-modal-wrapper"
        v-if="showImpactModal && impactResult"
        class="fixed inset-0 z-[10040] flex items-start justify-center bg-black/20 px-4 pt-16"
        @keydown.esc.stop.prevent="closeImpactModal"
        tabindex="0"
      >
        <div
          class="w-full max-w-3xl rounded-2xl bg-surface shadow-2xl border border-slate-200 overflow-hidden"
          @click.stop
        >
          <div class="max-h-[80vh] overflow-y-auto p-8">
            <div class="flex items-start gap-4 mb-6">
              <div class="mt-1 flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.16" />
                  <path
                    d="M10 6v5m0 3v0"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>

              <div class="flex-1">
                <h3 class="text-lg font-semibold text-slate-900 leading-snug">
                  Sua edição impacta anotações existentes
                </h3>
                <p class="mt-2 text-sm text-slate-600 leading-snug">
                  O texto que você enviou altera trechos ligados a algumas
                  notas. Marque as que deseja manter (ficarão avulsas, sem
                  vínculo com o texto). As desmarcadas serão apagadas
                  definitivamente.
                </p>
              </div>
            </div>

            <div
              v-if="criticalImpactedNotes.length"
              class="border border-slate-200 rounded-lg bg-slate-50 px-3 py-2 max-h-[16rem] overflow-auto"
            >
              <p class="text-xs font-semibold text-slate-600 mb-2">
                Notas afetadas. Quero manter as seguintes:
              </p>

              <ul class="space-y-1.5 text-sm text-slate-800">
                <li
                  v-for="note in criticalImpactedNotes"
                  :key="note.noteId"
                  class="flex flex-col rounded-md bg-surface/70 border border-slate-200 px-2.5 py-2"
                >
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-2 min-w-0">
                      <input
                        type="checkbox"
                        class="h-3.5 w-3.5 text-emerald-600 border-slate-300 rounded"
                        v-model="noteDecisions[note.noteId]"
                      />

                      <div class="flex flex-col min-w-0">
                        <span class="text-[10px] text-slate-400 leading-tight">
                          #{{ note.noteId }}
                        </span>
                        <span class="font-medium truncate leading-tight">
                          {{ note.noteTitle || "Nota sem título" }}
                        </span>
                      </div>
                    </div>

                    <span
                      class="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200"
                    >
                      Trecho alterado
                    </span>
                  </div>

                  <p class="mt-1 text-xs text-slate-600 leading-snug">
                    O trecho do texto ao qual esta nota se refere foi alterado.
                  </p>
                </li>
              </ul>
            </div>

            <div class="mt-6 flex flex-col gap-3">
              <p class="text-sm text-slate-600 leading-relaxed">
                Você pode revisar o texto e ajustar a edição para não afetar
                essas notas, ou prosseguir mesmo assim. Se prosseguir, as notas
                marcadas serão mantidas como anotações avulsas (sem vínculo com
                o trecho do texto) e as desmarcadas serão apagadas
                definitivamente.
              </p>
            </div>

            <div class="mt-8 flex justify-end gap-3">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeImpactModal"
                :disabled="isSubmitting"
              >
                Voltar e revisar texto
              </button>

              <button
                type="button"
                class="btn btn-primary"
                @click="confirmApplyTextEdit"
                :disabled="isSubmitting"
              >
                <svg
                  v-if="isSubmitting"
                  class="mr-1 -ml-1 w-5 h-5 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>

                <span>
                  {{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div
      v-if="showPlanCatalogModal"
      class="fixed inset-0 z-[10050] bg-black/20 flex justify-center items-start p-2 sm:p-3 overflow-hidden"
    >
      <div
        class="relative w-full max-w-[1344px] h-[94vh] overflow-y-auto self-start bg-[var(--bg-main,#f9fafb)] rounded-2xl shadow-2xl p-0"
        @click.stop
      >
        <PlanCatalogByName
          class="h-full"
          @close="handlePlanCatalogClose"
          @plan-selected="handlePlanSelect"
        />
      </div>
    </div>
  </Teleport>

  <TopicCardModal
    :show="showTopicCardModal"
    :topicId="selectedTopicIdForModal"
    @close="closeTopicCard"
  />

  <!-- Ensaio + confirmação da conversão para HTML, disparado ao importar
       EPUB/DOCX num texto ainda plaintext — ver processFiles. -->
  <ConvertTextToHtmlModal
    :show="showConvertModal"
    :textId="props.textId"
    @close="handleConvertModalClose"
    @converted="handleFileImportConverted"
  />
</template>

<style scoped>

/* =========================
   ERROS INLINE E VALIDAÇÃO
========================= */
.input-error {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}

.ms-error :deep(.multiselect__tags) {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}

.textarea-error :deep(textarea),
.textarea-error :deep(.ql-container) {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}

.error-msg {
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 0.3rem;
  display: block;
}

/* O `.input-base` global (main.css) fixa `height: 34px`, e altura fixa em
   textarea ANULA o atributo `rows` — era isso que deixava a sinopse com uma
   linha só. `height: auto` devolve o comando ao `rows`.

   A regra escopada vence a global por especificidade: o Vue a compila como
   `.synopsis-input[data-v-hash]`, com um seletor a mais que `.input-base`.
   Por isso a correção mora aqui e não em main.css, que serve todos os inputs
   do aplicativo. */
.synopsis-input {
  height: auto;
  /* Só na vertical: arrastar na horizontal estouraria a largura do painel. */
  resize: vertical;
}

/* Erro à esquerda, contador à direita, na mesma linha abaixo da textarea.
   O <span> vazio no lugar do erro mantém o contador encostado à direita
   mesmo sem erro — `justify-content: space-between` com um filho só o
   jogaria para a esquerda. */
.synopsis-footer {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.synopsis-counter {
  flex: 0 0 auto;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: var(--text-subtle);
}

.synopsis-counter--near {
  color: #b45309;
  font-weight: 600;
}

.modal-panel {
  height: calc(100vh - 20px - 2rem);
  max-height: calc(100vh - 20px - 2rem);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-header-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

/* Substitui o DetailedStepperText: a mesma informação (passo atual / total e
   o título do passo), só que compacta no cabeçalho em vez de uma barra própria. */
.modal-step-indicator {
  font-size: 0.75rem;
  color: var(--text-light);
  margin: 0;
}

.modal-footer {
  bottom: 0;
}

/* `gap` de 0.85rem, não 1.25rem: são vários intervalos entre os grupos do step,
   então cada 0.1rem aqui vale meia linha no fim. O alvo é o formulário caber
   inteiro — "Adicionar detalhes" incluído — mesmo com o arquivo carregado. */
.step-content {
  /* Espelho em CSS da constante EDITOR_HEIGHT do script. As duas precisam bater:
     o editor rich text recebe a altura por prop, e a caixa de texto puro não tem
     prop equivalente — pega daqui. */
  --editor-height: 14rem;

  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 0;
  flex: 1 1 auto;
}

.form-group-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

/* O nome do arquivo destacado dentro do rótulo: mesmo roxo dos interruptores de
   modo do sistema. Ele é o único trecho variável da frase, e a cor é o que o
   separa do texto fixo sem precisar de peso nem de aspas. */
.body-label-row__file {
  color: #4f46e5;
  font-weight: 700;
}

/* Ícone de espera dentro do rótulo do editor, enquanto o PDF é extraído. */
.body-label-row__spinner {
  margin-right: 0.35rem;
  font-size: 0.8rem;
  opacity: 0.8;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-200);
  transition: 0.25s;
  border-radius: 999px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  top: 3px;
  background-color: var(--surface);
  transition: 0.25s;
  border-radius: 50%;
}
.toggle-switch input:checked + .slider {
  background-color: var(--brand-light);
}
.toggle-switch input:checked + .slider:before {
  transform: translateX(16px);
}

.sys-badge {
  margin-left: 8px;
}

.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 7px;
  border: 0px solid var(--neutral-400);
  background: var(--surface-3);
  font-size: 0.85rem;
  margin: 2px 6px 2px 0;
}

.ms-chip-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;
  color: var(--text-muted);
}
.ms-chip-remove:hover {
  color: #0f172a;
}

.ms-chip-link {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  line-height: 1.1;
  text-decoration: none;
}

.ms-chip-link:hover {
  color: var(--link);
  text-decoration: underline;
}

.ms-chip-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  border-radius: 6px;
}

.ms-option,
.ms-single {
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.ms-label {
  line-height: 1.2;
}

/* Mede o conteúdo: a altura do editor agora é fixa e vem da prop (EDITOR_HEIGHT),
   não da disputa por espaço no painel. Era `flex: 1 1 auto; min-height: 14rem`,
   de quando o editor esticava — mantê-lo esticando deixaria um vão embaixo da
   caixa fixa. */
.form-group--editor {
  flex: 0 0 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* ── Expandido: quem rola é o EDITOR, não o corpo do modal ─────────────────

   `overflow: hidden` aqui é a peça que faltava. Enquanto o .modal-body seguia
   `overflow-y: auto`, ele nunca limitava a altura dos filhos: o editor crescia
   com o conteúdo, o corpo do modal rolava no lugar dele, e a caixa expandida
   aparecia SEM barra de rolagem própria — o `overflow-y: auto` do .rte-content
   nunca chegava a disparar porque nada o obrigava a caber.

   Fechado o corpo, a altura desce pela cadeia flex e o editor volta a ter teto.
   É o mesmo arranjo do AddActivityForm. */
.modal-body--expanded {
  overflow: hidden;
}

/* Expandido é a exceção: aí o editor volta a esticar, que é o que o botão
   promete. A prop devolve piso zero e teto nenhum; aqui a cadeia flex volta a
   poder crescer. */
.step-content--expanded .form-group--editor {
  flex: 1 1 auto;
}

/* A mesma cadeia no modo arquivo: o grupo é `flex: 0 0 auto` em repouso (mede o
   conteúdo), e precisa voltar a esticar quando expandido, ou o editor da prévia
   não recebe altura nenhuma para preencher. */
.step-content--expanded .form-group--file,
.step-content--expanded .loaded-file-preview {
  flex: 1 1 auto;
  min-height: 0;
}

.step-content--expanded .loaded-file-preview :deep(.rte) {
  flex: 1 1 auto;
  min-height: 0;
}

/* ── Modo arquivo ───────────────────────────────────────────────────────────

   `flex: 0 0 auto`: o grupo mede o conteúdo, e nada mais tenta esticá-lo.

   Isto era `flex: 1 1 auto; min-height: 14rem`, de quando a zona de arrasto
   precisava ocupar o painel para não deixar buraco. Com ela fora, esticar não
   tem mais para quê — e o `flex-shrink: 1` que vinha junto era o que espremia o
   grupo abaixo do conteúdo e fazia o editor transbordar por cima da linha de
   visibilidade. Quem rola, se precisar, é o .modal-body. */
.form-group--file {
  flex: 0 0 auto;
  min-height: 0;

  display: flex;
  flex-direction: column;
}

/* Este arquivo não tinha regra própria para a prévia — o bloco caía no layout
   padrão. Explicitar aqui iguala o espaçamento ao do AddTextForm e dá o mesmo
   controle sobre a folga que o resto desta faixa precisou ceder. */
/* Sem `margin-top`, e com o mesmo `gap` do `.form-group`: é o que põe o rótulo
   "Conteúdo" na MESMA altura nos dois modos. O recuo de 0.25rem que havia aqui
   era justamente o que fazia ele nascer alguns pixels abaixo do rótulo do modo
   colar, onde a linha é o primeiro filho do grupo. */
.loaded-file-preview {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-top: 0;
}

/* ── A caixa de texto puro tem a altura útil do editor colapsado ─────────────

   O ramo HTML desta prévia usa o RichTextEditor com as MESMAS props do
   AddTextForm, então lá as alturas já batem. O ramo plaintext é que destoava: o
   CustomTextarea traz `min-height: 200px` e cresce, e o mesmo arquivo abria este
   formulário bem mais alto que o de cadastro.

   O valor é o `collapsed-height` do editor com o `em` já resolvido: 4 linhas de
   1.05rem a line-height 1.55, mais o padding vertical de 1.7rem. Fixo, e não em
   `em`, porque aqui a fonte é a do textarea, não a de `.text-body--html` — a
   mesma expressão daria outro número.

   `min-height: 0` anula o piso do componente; `height` fecha a caixa e entrega o
   resto à rolagem interna do próprio textarea. */
.file-preview-textarea :deep(.custom-textarea) {
  min-height: 0;
  height: var(--editor-height);
  resize: vertical;
}

.editor-container {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.editor-textarea {
  flex: 1 1 auto;
  min-height: 0;
}

/* O ramo de texto puro segue a MESMA altura fixa do editor rich text. Sem isto
   ele ficava com o `min-height: 200px` do componente e crescia com o conteúdo —
   a caixa mudava de tamanho conforme o texto, que é justamente o que a altura
   fixa veio acabar. */
.editor-textarea :deep(.custom-textarea) {
  min-height: 0;
  height: var(--editor-height);
  resize: vertical;
}

/* Expandido, ela volta a esticar junto com o resto — o `height: 100%` só faz
   sentido quando há um pai de altura definida crescendo, que é o caso ali. */
.step-content--expanded .editor-textarea :deep(.custom-textarea) {
  flex: 1 1 auto;
  height: 100%;
  resize: none;
}

/* 🔹 NOVO: no modo expandido o editor ocupa todo o painel do step 2 */
.step-content--expanded {
  overflow: hidden;
}

/* 🔹 NOVO: linha do label + botão de expandir (espelho do AddNoteForm)
   Rótulo à esquerda, botão de expandir encostado à direita. */
.body-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
}

/* Recolhido não há rótulo aqui — o editor já vem sob o "Conteúdo" do grupo do
   topo —, e `space-between` com um filho só o jogaria para a esquerda. */
.body-label-row--end {
  justify-content: flex-end;
}

/* O nome do arquivo entra no rótulo e pode ser longo: ele corta com reticências
   em vez de empurrar os botões para fora da linha. */
.body-label-row__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O aviso de extração é uma frase, não um nome: ele quebra em vez de ser cortado
   pelas reticências, que engoliriam justamente a parte que pede paciência. */
.body-label-row__label--busy {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.body-label-row__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

/* 🔹 NOVO: botão expandir/colapsar (mesmo visual do AddNoteForm) */
.ct-expand-btn-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: none;
  background: var(--neutral-200);
  color: var(--text-dark);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.18s ease-in-out;
  flex-shrink: 0;
}
.ct-expand-btn-inline:hover:not(:disabled) {
  background: var(--neutral-400);
  transform: translateY(-1px);
}
.ct-expand-btn-inline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── "Mais informações": a catalogação, recolhida por padrão ─────────────── */
.details-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease;
}
.details-toggle:hover:not(:disabled) {
  color: var(--text-brand);
}
.details-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.details-toggle__chevron {
  font-size: 0.7rem;
  transition: transform 0.2s ease-in-out;
}
.details-toggle--open .details-toggle__chevron {
  transform: rotate(180deg);
}

.details-panel {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 0.25rem;
  padding-top: 0.9rem;
  /* Respiro depois do último campo (Assuntos): a área rolável termina rente ao
     rodapé, e o padding do .modal-body sozinho não dá folga no fim da rolagem. */
  padding-bottom: 0.9rem;
  border-top: 1px dashed var(--neutral-200);
}

.details-panel__hint {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--text-muted);
}
.details-panel__hint i {
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* O toggle "Texto Ativo" vinha de uma tela onde ficava sozinho no topo; aqui
   ele é só mais um detalhe da lista, sem o recuo negativo de lá. */
.details-panel .form-group-toggle {
  justify-content: flex-start;
  padding: 0;
}

.access-segment {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.25rem;
}

/* Ocupa a largura inteira da coluna: com flex: 1 (abaixo) os botões ficam do
   mesmo tamanho e esticam até o rótulo caber em uma linha só. */
.access-segment--sm {
  width: 100%;
}

/* ── Conteúdo e visibilidade como duas colunas ──────────────────────────────
   Rótulos numa linha, controles na seguinte. As duas linhas usam a MESMA
   largura de coluna à direita, e é isso que faz o rótulo "Visibilidade" cair
   exatamente sobre o gatilho.

   A largura sai do rótulo mais longo do menu, "Personalizado": abaixo disso o
   gatilho mudaria de tamanho ao trocar de modo, e a linha inteira dançaria a
   cada escolha. */
.content-label-row,
.content-method-row {
  /* O gatilho: largura do rótulo mais longo do menu, "Personalizado". */
  --visibility-trigger-w: 11.5rem;
  /* Faixa RESERVADA à direita do gatilho para o "Editar permissões". Fica
     reservada mesmo quando o link não existe (modos público e privado): sem
     isso o gatilho saltaria de lugar a cada troca de modo. */
  --visibility-actions-w: 9rem;
  --visibility-gap: 0.5rem;
  --visibility-col: calc(
    var(--visibility-trigger-w) + var(--visibility-gap) +
      var(--visibility-actions-w)
  );

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.content-label-row {
  align-items: baseline;
}

/* O rótulo da direita não é `<label for>` porque o gatilho é um botão, não um
   campo: quem faz a associação é o `aria-labelledby` do botão. */
.content-label-row .form-label {
  margin: 0;
}

.visibility-col {
  flex: 0 0 auto;
  width: var(--visibility-col);
}

/* Anula o `width: 100%` da regra base: aqui o segmento divide a linha, e largura
   cheia empurraria a visibilidade para a linha de baixo sempre. `flex: 1 1 auto`
   mantém o que aquela regra queria — os botões esticando até o rótulo caber em
   uma linha só — dentro do espaço que sobra. */
.content-method-row .access-segment--sm {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
  /* A margem existia para descolar do rótulo logo acima; agora quem separa é o
     `gap` da coluna, e mantê-la desalinharia o segmento do gatilho ao lado. */
  margin-top: 0;
}

/* Linha: gatilho à esquerda da coluna, "Editar permissões" na faixa reservada à
   direita dele. Como a coluna inteira é mais larga que o gatilho, ela começa
   mais à esquerda — e o rótulo "Visibilidade", que tem a mesma largura de
   coluna e alinha à esquerda, cai exatamente sobre o gatilho. */
.content-method-row .visibility-inline {
  flex-direction: row;
  align-items: center;
  gap: var(--visibility-gap);
}

.content-method-row .visibility-dropdown {
  flex: 0 0 auto;
  width: var(--visibility-trigger-w);
}

/* Ocupa a faixa reservada e quebra em duas linhas se precisar, em vez de
   empurrar o gatilho. */
.content-method-row .access-hint__link {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  line-height: 1.2;
}

/* `space-between` afasta o chevron do rótulo e o encosta na borda direita —
   num gatilho de largura fixa, agrupá-los no meio deixaria um vazio à direita
   que faria o botão parecer quebrado. */
.content-method-row .visibility-trigger {
  width: 100%;
  justify-content: space-between;
}

/* O rótulo do modo fica com o espaço que sobra entre ícone e chevron, e corta
   com reticências em vez de estourar a coluna. */
.content-method-row .visibility-trigger__label {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O menu acompanha a coluna: mais estreito que o gatilho pareceria desencaixado,
   e é o `min-width` de 200px da regra base que o alargava sozinho. */
.content-method-row .visibility-menu {
  min-width: 100%;
}
 
.segment-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.6rem 0.5rem;
  border-radius: 12px;
  border: 1.5px solid var(--neutral-200);
  background-color: var(--surface-2);
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--text-muted);
}
 
/* Ícone e rótulo lado a lado em vez de empilhados, para caber na altura fixa.

   32px, a MESMA do `.visibility-trigger` ao lado: eles dividem a linha, e 4px de
   diferença bastavam para as duas colunas não assentarem no mesmo eixo. Já foram
   38px, quando o par era o elemento mais pesado da faixa; a redução continua
   valendo, só que agora parando onde o vizinho para. */
.access-segment--sm .segment-btn {
  flex-direction: row;
  height: 32px;
  padding: 0 0.45rem;
  border-radius: 7px;
  gap: 0.3rem;
  font-size: 0.78rem;
  /* Ícone + rótulo centralizados no botão, que é mais largo que o conteúdo. */
  justify-content: center;
}
 
.segment-btn:hover:not(:disabled):not(.segment-btn--active) {
  border-color: var(--neutral-400);
  background-color: var(--surface-3);
  color: var(--text-dark);
  transform: translateY(-1px);
}
 
.segment-btn:disabled { opacity: 0.5; cursor: not-allowed; }
 
.segment-btn__icon { font-size: 1rem; line-height: 1; }
.access-segment--sm .segment-btn__icon { font-size: 0.78rem; }
 
.segment-btn__label { font-size: 0.82rem; font-weight: 600; line-height: 1; }
.access-segment--sm .segment-btn__label {
  font-size: 0.75rem;
  /* A altura do botão é fixa (32px): o rótulo estica na horizontal em vez de
     quebrar em duas linhas. */
  white-space: nowrap;
}
 
/* Sem semântica de cor própria (público/privado/personalizado) — usado no
   seletor "Método de Entrada", que só alterna entre duas opções neutras. */
.segment-btn--neutral.segment-btn--active {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
  box-shadow: 0 2px 8px rgba(155, 132, 255, 0.15);
}

/* ── Visibilidade condensada: "Visibilidade: Público ▾" ──────────────────────
   Num cadastro rápido a visibilidade é uma escolha secundária, com um padrão
   já bom (público). Ela cabe numa linha de texto, não num campo de formulário
   do tamanho do conteúdo. ── */
/* `flex-end`, e não `space-between`: com a visibilidade movida para a linha do
   Conteúdo, sobrou só o "Adicionar detalhes" aqui, e `space-between` com um
   filho só o jogaria para a esquerda. Ele continua onde sempre esteve. */
.quick-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.15rem;
}

.visibility-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-dark);
}

.visibility-dropdown {
  position: relative;
}

/* Gatilho do tamanho do próprio rótulo — é uma escolha, não um campo. */
.visibility-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 32px;
  padding: 0 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--surface);
  color: var(--text-dark);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.visibility-trigger:hover:not(:disabled) {
  border-color: var(--focus-border);
  background-color: #f5f8ff;
}
.visibility-trigger.active {
  border-color: var(--focus-border);
}
.visibility-trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.visibility-trigger__icon {
  font-size: 0.85rem;
}

.visibility-trigger__chevron {
  font-size: 0.6rem;
  opacity: 0.7;
  transition: transform 0.2s ease-in-out;
}
.visibility-trigger__chevron.open {
  transform: rotate(180deg);
}

/* Abre para BAIXO, sempre.

   Antes abria para cima (`bottom: calc(100% + 4px)`), para o painel não nascer
   fora da parte visível do .modal-body. O preço era ele subir POR CIMA do editor
   e cobrir o texto que se acabou de revisar — e, com o corpo rolado, parecer que
   o menu escapava junto com o conteúdo.

   Abrindo para baixo o painel fica sempre abaixo do editor, onde nada tapa. O
   recorte que motivava a versão anterior é resolvido no gatilho, que rola o
   painel para dentro da vista ao abrir (ver toggleVisibilityMenu). */
.visibility-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  z-index: 100;
  min-width: 200px;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
}
.visibility-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}

.visibility-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.85rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out;
}
/* A cor do modo só aparece no hover e na opção atual — no repouso a lista é
   neutra, como a do dropdown de progresso do TextDisplayer. */
.visibility-option:not(:hover):not(.visibility-option--current) {
  background-color: transparent;
  color: var(--text-dark);
}
.visibility-option--current {
  font-weight: 700;
}
.visibility-option__label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.visibility-option__icon {
  width: 0.95rem;
  text-align: center;
  font-size: 0.8rem;
}
.visibility-option__check {
  color: var(--brand);
  font-size: 0.75rem;
}

.visibility--public {
  background-color: var(--success-bg);
  color: var(--success-text);
}
.visibility--private {
  background-color: var(--warning-bg);
  color: var(--warning-text);
}
.visibility--custom {
  background-color: var(--brand-tint);
  color: var(--text-brand);
}

/* Declarado depois dos .visibility--* de propósito: no ícone vale a cor, nunca
   o fundo em pílula. */
.visibility-trigger__icon,
.visibility-option__icon {
  background-color: transparent;
}

/* ── Confirmação do salvar, quando ainda falta catalogação ───────────────── */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  flex: 1 1 auto;
}

.success-state__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 0.25rem;
  border-radius: 50%;
  background-color: var(--success-bg);
  color: var(--success);
  font-size: 1.4rem;
}

.success-state__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-dark);
}

.success-state__text {
  font-size: 0.9rem;
  color: var(--text-light);
  max-width: 34rem;
}

.success-state__hint {
  font-size: 0.8rem;
  color: var(--text-muted);
  max-width: 34rem;
}

.success-state__pending-label {
  margin-top: 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-subtle);
}

.success-pending {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

/* Cada item é um atalho, não um aviso: leva direto ao campo que falta, com o
   cursor nele. Daí a seta — e o cursor de mão em cima do rótulo. */
.pending-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--neutral-200);
  background: var(--surface-2);
  color: var(--text-light);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    color 0.15s ease, transform 0.05s ease;
}
.pending-chip:hover {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
}
.pending-chip:active {
  transform: translateY(1px);
}

.pending-chip__icon {
  font-size: 0.85rem;
}

/* O fa-book-open desenha o livro na metade de cima do quadro e assenta acima da
   linha do rótulo; 1px devolve o alinhamento óptico com os outros dois. */
.pending-chip__icon.fa-book-open {
  transform: translateY(1px);
}

.pending-chip__go {
  font-size: 0.7rem;
  opacity: 0.55;
}

.access-hint__link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--brand);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.access-hint__link:hover:not(:disabled) { color: #7c63f5; }
.access-hint__link:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-access-toggle:hover:not(:disabled) {
  background-color: var(--neutral-300);
  transform: scale(1.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.btn-access-toggle:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-access-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* =========================
   MINI-MODAL DE PERMISSÕES
========================= */
.permission-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background-color: rgba(15, 23, 42, 0.2);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  padding-left: 1rem;
  padding-right: 1rem;
}

.permission-modal-panel {
  width: 590px;
  max-width: 100%;
  height: 760px;
  max-height: calc(100vh - 100px);
  background-color: var(--bg-content);
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.permission-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}

/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.permission-modal-close {
  color: var(--text-light);
  background-color: transparent;
  border-radius: 8px;
  padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}
.permission-modal-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}

.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
  min-height: 0;
  overflow: hidden;
}

.permission-modal-instruction {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
  line-height: 1.35;
  display: block;
}

.permission-modal-footer {
  flex: 0 0 auto;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-header);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

:deep(.permission-form-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

:deep(.mt-5) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem !important;
}

:deep(.table-wrap) {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
  background: var(--bg-content);
  margin-top: 0.5rem;
}

:deep(.table-wrap::-webkit-scrollbar) { width: 6px; }
:deep(.table-wrap::-webkit-scrollbar-thumb) {
  background-color: var(--neutral-400);
  border-radius: 10px;
}

.permission-modal-fade-enter-active,
.permission-modal-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.permission-modal-fade-enter-from,
.permission-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
/* =========================
   LOADING OVERLAY
========================= */





/* ── Seletor de capa ────────────────────────────────────────────────────────
   A miniatura tem a MESMA proporção do card (3:4): o que o usuário vê aqui é
   exatamente o recorte que vai para a estante, e não uma aproximação. */
.cover-picker {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.cover-preview {
  flex: 0 0 auto;
  width: 84px;
  aspect-ratio: 3 / 4;
  border-radius: 6px;
  border: 1px dashed var(--border-color);
  background: var(--surface-2);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-preview__empty {
  color: var(--neutral-400);
  font-size: 1.4rem;
}

.cover-picker__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  min-width: 0;
}

/* <label> e não <button>: é o rótulo do input de arquivo, que fica escondido —
   é o que permite estilizar sem depender do botão nativo do navegador. */
.btn-cover {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.7rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--surface);
  color: var(--text-dark);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.btn-cover:hover {
  background: var(--surface-3);
}

.btn-cover--busy {
  opacity: 0.7;
  pointer-events: none;
}

.btn-cover--ghost {
  border-color: var(--danger-bg);
  background: var(--danger-bg);
  color: var(--danger-text);
}

.btn-cover--ghost:hover {
  background: var(--danger-bg);
}

/* O input existe e recebe o clique pelo <label>; escondê-lo com display:none o
   tiraria da navegação por teclado. */
.cover-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.cover-hint {
  margin: 0;
  font-size: 0.72rem;
  color: var(--text-subtle);
}
.cover-hint--warn {
  color: #b45309;
  font-weight: 600;
}
</style>
